import { useState, useRef, useEffect } from "react";
import {
  processInvoices, parseVatExcel,
  saveExcelToHistory,
  getMyClients, tagReportToClient, saveSalesSnapshot, saveSalesExcel,
  processSalesInvoices,
  processMultiReceiptsPurchases, processMultiReceiptsSales,
  processQuickbooksReport, mergeExcelFiles,
  processZReports,
} from "./api";
import { useInactivityLogout, clearActivityTimestamp } from "./useInactivityLogout";
import { generateVatExcel, generateSalesExcel } from "./excelGenerator";
import VatWorkflow from "./VatWorkflow";
import BankStatementWorkflow from "./BankStatementWorkflow";
import PageUsage from "./PageUsage";
import ConnectedAccounts from "./ConnectedAccounts";
import ClientsManager from "./ClientsManager";
import ClientProfile from "./ClientProfile";
import ConfirmModal from "./ConfirmModal";
import PersistentRoute from "./PersistentRoute";
import { useProcessingStatus } from "./useProcessingGuard";

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  :root {
    --bg:#03050f; --surface:#080f22; --panel:#0c1628;
    --border:rgba(255,255,255,0.07); --border-blue:rgba(59,158,255,0.2);
    --ai-blue:#3b9eff; --ai-bright:#60b8ff; --ai-deep:#1a6fd4;
    --text:#eef4ff; --muted:#5a7a99; --muted-light:#8aabb8;
    --success:#22d3a8; --warning:#f59e0b; --error:#ff6b8a;
    --purple:#7c3aed; --teal:#0ea5e9;
  }
  html,body,#root { height:100%; background:var(--bg); color:var(--text); font-family:'Outfit',sans-serif; overflow-x:hidden; }
  .app { display:flex; min-height:100vh; }
  .main { margin-left:220px; flex:1; display:flex; flex-direction:column; min-height:100vh; }
  .page { padding:24px; flex:1; }

  /* Sidebar */
  .sidebar { width:220px; background:var(--surface); border-right:1px solid var(--border); display:flex; flex-direction:column; position:fixed; top:0; left:0; bottom:0; z-index:100; transition:transform 0.3s ease; }
  .sidebar-brand { padding:20px 20px 16px; border-bottom:1px solid var(--border); }
  .brand-name { font-size:16px; font-weight:800; letter-spacing:-0.02em; }
  .brand-tag { font-size:9px; color:var(--ai-blue); letter-spacing:0.1em; text-transform:uppercase; margin-top:2px; }
  .sidebar-nav { padding:12px 10px; flex:1; overflow-y:auto; }
  .nav-label { font-size:9px; font-weight:600; color:var(--muted); letter-spacing:0.12em; text-transform:uppercase; padding:0 10px; margin:12px 0 6px; }
  .nav-item { display:flex; align-items:center; gap:10px; padding:9px 12px; border-radius:10px; cursor:pointer; font-size:13.5px; font-weight:500; color:var(--muted-light); transition:all 0.18s; margin-bottom:2px; border:1px solid transparent; user-select:none; }
  .nav-item:hover { background:rgba(59,158,255,0.07); color:var(--text); }
  .nav-item.active { background:rgba(59,158,255,0.12); color:var(--ai-bright); border-color:rgba(59,158,255,0.15); }
  .nav-item.parent-active { color:var(--text); background:rgba(59,158,255,0.05); }
  .nav-icon { font-size:15px; width:20px; text-align:center; flex-shrink:0; }
  .nav-chevron { margin-left:auto; font-size:11px; transition:transform 0.2s; color:var(--muted); }
  .nav-chevron.open { transform:rotate(90deg); color:var(--ai-blue); }
  .nav-sub-item { display:flex; align-items:center; gap:8px; padding:7px 12px 7px 36px; border-radius:9px; cursor:pointer; font-size:12.5px; font-weight:500; color:var(--muted); transition:all 0.15s; margin-bottom:1px; border:1px solid transparent; }
  .nav-sub-item:hover { background:rgba(59,158,255,0.06); color:var(--muted-light); }
  .nav-sub-item.active { background:rgba(59,158,255,0.1); color:var(--ai-bright); border-color:rgba(59,158,255,0.12); }
  .nav-sub-dot { width:5px; height:5px; border-radius:50%; background:currentColor; flex-shrink:0; }
  .sidebar-footer { padding:14px 16px; border-top:1px solid var(--border); }
  .user-pill { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
  .user-avatar { width:32px; height:32px; border-radius:50%; background:linear-gradient(135deg,var(--ai-deep),var(--ai-blue)); display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; color:#fff; flex-shrink:0; }
  .user-name { font-size:13px; font-weight:600; }
  .user-plan { font-size:11px; color:var(--ai-blue); }
  .logout-btn { display:flex; align-items:center; gap:6px; padding:7px 10px; background:transparent; border:1px solid var(--border); border-radius:8px; color:var(--muted); font-size:12px; font-family:'Outfit',sans-serif; cursor:pointer; width:100%; transition:all 0.2s; }
  .logout-btn:hover { border-color:var(--error); color:var(--error); }
  .sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,0.6); z-index:99; }

  /* Topbar */
  .topbar { min-height:56px; background:var(--surface); border-bottom:1px solid var(--border); display:grid; grid-template-columns:1fr auto 1fr; align-items:center; padding:10px 24px; position:sticky; top:0; z-index:50; gap:12px; }
  .topbar-left { display:flex; align-items:center; gap:12px; }
  .topbar-title { font-size:16px; font-weight:700; letter-spacing:-0.01em; }
  .topbar-brand { text-align:center; }
  .topbar-brand-name { font-size:21px; font-weight:800; letter-spacing:-0.02em; }
  .topbar-brand-tag { font-size:10px; color:var(--ai-blue); letter-spacing:0.12em; text-transform:uppercase; margin-top:2px; }
  .topbar-right { display:flex; align-items:center; gap:12px; justify-self:end; }
  .api-status { display:flex; align-items:center; gap:6px; font-size:12px; color:var(--muted-light); background:rgba(34,211,168,0.08); border:1px solid rgba(34,211,168,0.2); padding:5px 10px; border-radius:20px; }
  .status-dot { width:6px; height:6px; border-radius:50%; background:var(--success); animation:blink 2s ease infinite; }
  @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
  @keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.4);opacity:0.7}}
  .nav-busy-dot{width:7px;height:7px;border-radius:50%;background:var(--warning);flex-shrink:0;animation:pulse 1s ease-in-out infinite;margin-left:auto;}
  .hamburger { display:none; background:transparent; border:none; color:var(--text); font-size:20px; cursor:pointer; padding:4px; }

  /* Stats */
  .stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:24px; }
  .stat-card { background:var(--panel); border:1px solid var(--border); border-radius:16px; padding:18px 20px; animation:fadeUp 0.5s ease both; }
  .stat-card:nth-child(1){animation-delay:.05s}.stat-card:nth-child(2){animation-delay:.10s}
  .stat-card:nth-child(3){animation-delay:.15s}.stat-card:nth-child(4){animation-delay:.20s}
  @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
  .stat-label { font-size:11px; font-weight:600; color:var(--muted); letter-spacing:0.08em; text-transform:uppercase; margin-bottom:8px; }
  .stat-value { font-family:'JetBrains Mono',monospace; font-size:22px; font-weight:500; letter-spacing:-0.02em; }
  .stat-sub { font-size:11px; color:var(--muted); margin-top:4px; }
  .c-blue{color:var(--ai-blue)}.c-green{color:var(--success)}.c-warn{color:var(--warning)}.c-err{color:var(--error)}
  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
  .full-col { margin-bottom:20px; }

  /* Panels */
  .panel { background:var(--panel); border:1px solid var(--border); border-radius:16px; overflow:hidden; animation:fadeUp 0.5s ease 0.25s both; }
  .panel-header { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid var(--border); }
  .panel-title { font-size:13px; font-weight:700; color:var(--text); }
  .panel-body { padding:20px; }

  /* Upload */
  .upload-zone { border:2px dashed var(--border-blue); border-radius:14px; padding:28px 20px; text-align:center; cursor:pointer; transition:all 0.25s; background:rgba(59,158,255,0.02); }
  .upload-zone:hover,.upload-zone.drag-over { border-color:var(--ai-blue); background:rgba(59,158,255,0.06); }
  .upload-icon { font-size:28px; margin-bottom:8px; }
  .upload-title { font-size:14px; font-weight:600; margin-bottom:4px; }
  .upload-sub { font-size:12px; color:var(--muted); }
  .upload-sub span { color:var(--ai-blue); }
  .file-item { display:flex; align-items:center; gap:10px; padding:9px 12px; background:var(--surface); border:1px solid var(--border); border-radius:10px; margin-bottom:8px; font-size:13px; }
  .file-name { flex:1; font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .file-size { font-size:11px; color:var(--muted); }
  .file-remove { background:none; border:none; color:var(--muted); cursor:pointer; font-size:14px; padding:2px 4px; transition:color 0.2s; }
  .file-remove:hover { color:var(--error); }

  /* Buttons */
  .btn { display:flex; align-items:center; justify-content:center; gap:8px; padding:12px 16px; border:none; border-radius:11px; font-family:'Outfit',sans-serif; font-size:13.5px; font-weight:600; cursor:pointer; transition:all 0.22s; width:100%; }
  .btn:hover:not(:disabled){transform:translateY(-2px)}
  .btn:disabled{opacity:0.45;cursor:not-allowed;transform:none}
  .btn-blue{background:linear-gradient(135deg,var(--ai-deep),var(--ai-blue));color:#fff;box-shadow:0 4px 20px rgba(59,158,255,0.3)}
  .btn-blue:hover:not(:disabled){box-shadow:0 8px 28px rgba(59,158,255,0.4)}
  .btn-green{background:linear-gradient(135deg,#0f6e56,#22d3a8);color:#fff;box-shadow:0 4px 20px rgba(34,211,168,0.2)}
  .btn-teal{background:linear-gradient(135deg,#0369a1,#0ea5e9);color:#fff}
  .btn-purple{background:linear-gradient(135deg,#5b21b6,#7c3aed);color:#fff}
  .btn-ghost{background:rgba(255,255,255,0.05);color:var(--muted-light);border:1px solid var(--border)}
  .btn-ghost:hover{background:rgba(255,255,255,0.09);color:var(--text)}
  .btn-danger{background:linear-gradient(135deg,#991b1b,#ef4444);color:#fff}
  .btn-sm{padding:8px 14px;font-size:12px;border-radius:9px;width:auto}

  /* Circular progress */
  .circ-wrap { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:20px 0 10px; }
  .circ-svg { transform:rotate(-90deg); }
  .circ-track { fill:none; stroke:rgba(59,158,255,0.12); }
  .circ-fill { fill:none; stroke:url(#blueGrad); stroke-linecap:round; transition:stroke-dashoffset 0.6s cubic-bezier(0.4,0,0.2,1); filter:drop-shadow(0 0 6px rgba(59,158,255,0.5)); }
  .circ-center { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); display:flex; flex-direction:column; align-items:center; }
  .circ-pct { font-family:'JetBrains Mono',monospace; font-size:22px; font-weight:700; color:var(--text); line-height:1; }
  .circ-unit { font-size:10px; color:var(--muted); margin-top:2px; }
  .circ-container { position:relative; display:inline-block; }
  .circ-stage { font-size:12px; color:var(--ai-bright); text-align:center; margin-top:10px; font-weight:500; letter-spacing:0.02em; min-height:18px; }

  /* Log */
  .log-box { background:#040b18; border:1px solid var(--border); border-radius:10px; padding:12px; font-family:'JetBrains Mono',monospace; font-size:11px; color:#7bc8f6; height:100px; overflow-y:auto; line-height:1.7; margin-top:12px; }
  .log-ok{color:var(--success)}.log-err{color:var(--error)}

  /* Tables */
  .inv-table { width:100%; border-collapse:collapse; font-size:13px; }
  .inv-table th { text-align:left; padding:0 14px 10px; font-size:10px; font-weight:600; color:var(--muted); letter-spacing:0.08em; text-transform:uppercase; border-bottom:1px solid var(--border); }
  .inv-table td { padding:11px 14px; border-bottom:1px solid rgba(255,255,255,0.04); color:var(--muted-light); vertical-align:middle; }
  .inv-table tr:last-child td{border-bottom:none}
  .inv-table tr:hover td{background:rgba(59,158,255,0.04);color:var(--text)}
  .sup-name{font-weight:600;color:var(--text)}
  .inv-num{font-family:'JetBrains Mono',monospace;font-size:12px;color:var(--ai-blue)}
  .inv-amt{font-family:'JetBrains Mono',monospace;font-size:13px;color:var(--text);font-weight:500}

  /* Badges */
  .badge{display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:600}
  .badge-dot{width:5px;height:5px;border-radius:50%;background:currentColor}
  .badge-high{background:rgba(34,211,168,0.1);color:var(--success);border:1px solid rgba(34,211,168,0.2)}
  .badge-medium{background:rgba(245,158,11,0.1);color:var(--warning);border:1px solid rgba(245,158,11,0.2)}
  .badge-low{background:rgba(255,107,138,0.1);color:var(--error);border:1px solid rgba(255,107,138,0.2)}

  /* Form fields */
  .field{margin-bottom:12px}
  .field label{display:block;font-size:10px;font-weight:600;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:6px}
  .field input,.field select{width:100%;padding:10px 13px;background:rgba(59,158,255,0.04);border:1px solid var(--border);border-radius:10px;color:var(--text);font-family:'Outfit',sans-serif;font-size:14px;outline:none;transition:border-color 0.2s,box-shadow 0.2s}
  .field input:focus,.field select:focus{border-color:var(--ai-blue);background:rgba(59,158,255,0.07);box-shadow:0 0 0 3px rgba(59,158,255,0.1)}
  .field input::placeholder{color:rgba(90,122,153,0.5)}
  .field select{appearance:none;cursor:pointer}
  .field select option{background:#0c1628}

  /* VAT return */
  .vat-row{display:flex;align-items:center;justify-content:space-between;padding:9px 0;border-bottom:1px solid var(--border);font-size:13px}
  .vat-row:last-child{border-bottom:none}
  .vat-row-label{color:var(--muted-light)}
  .vat-row-val{font-family:'JetBrains Mono',monospace;font-size:13px;font-weight:500;color:var(--text)}
  .vat-highlight{background:rgba(59,158,255,0.06);border-radius:8px;padding:9px 12px;margin:3px 0}
  .net-bar{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;background:rgba(59,158,255,0.07);border:1px solid var(--border-blue);border-radius:14px;margin-top:14px}
  .net-label{font-size:14px;font-weight:600}
  .net-value{font-family:'JetBrains Mono',monospace;font-size:24px;font-weight:700;color:var(--warning)}
  .net-value.refund{color:var(--success)}
  .import-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:20px;font-size:11px;font-weight:500;background:rgba(59,158,255,0.08);border:1px solid rgba(59,158,255,0.15);color:var(--muted-light);margin-bottom:4px}
  .import-pill.loaded{background:rgba(34,211,168,0.08);border-color:rgba(34,211,168,0.2);color:var(--success)}
  .sec-header{padding:10px 16px;font-size:12px;font-weight:700;letter-spacing:0.04em;color:#fff}
  .sec-a{background:rgba(15,110,86,0.3)}.sec-b{background:rgba(12,68,124,0.35)}.sec-c{background:rgba(59,158,255,0.12)}
  .divider{height:1px;background:var(--border);margin:14px 0}
  .summary-lbl{font-size:13px;font-weight:700;color:var(--success);text-align:center;margin-top:12px;line-height:1.8}
  .empty-state{padding:40px 20px;text-align:center;color:var(--muted);font-size:13px}
  .empty-icon{font-size:32px;margin-bottom:10px;opacity:0.4}

  /* VAT Workflow (ERS document generator) */
  .vat-workflow{display:flex;flex-direction:column;gap:16px;animation:fadeUp 0.5s ease both}
  .vat-step{background:var(--panel);border:1px solid var(--border);border-radius:14px;padding:16px 18px}
  .vat-step-label{font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:var(--ai-bright);margin-bottom:14px}
  .vat-step-hint{font-size:11px;font-weight:500;letter-spacing:0;text-transform:none;color:var(--muted)}

  .doc-selector-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px}
  .doc-card{position:relative;padding:14px;border-radius:12px;cursor:pointer;user-select:none;border:2px solid var(--border);background:rgba(59,158,255,0.02);transition:all 0.18s}
  .doc-card:hover{border-color:var(--border-blue);background:rgba(59,158,255,0.05)}
  .doc-card--selected{border-color:var(--ai-blue);background:rgba(59,158,255,0.08)}
  .doc-card-check{position:absolute;top:8px;right:10px;width:18px;height:18px;border-radius:50%;background:var(--ai-blue);color:#fff;font-size:11px;display:flex;align-items:center;justify-content:center;font-weight:700;opacity:0;transition:opacity 0.15s}
  .doc-card--selected .doc-card-check{opacity:1}
  .doc-card-icon{font-size:22px;margin-bottom:6px}
  .doc-card-title{font-size:13px;font-weight:700;color:var(--text);margin-bottom:4px}
  .doc-card-sub{font-size:11px;color:var(--muted);line-height:1.4;white-space:pre-line}

  .file-upload-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
  .file-upload-name{font-size:12px;color:var(--muted)}
  .file-upload-name--set{color:var(--success);font-weight:500}

  .taxpayer-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px}
  .field-group{display:flex;flex-direction:column}
  .field-label{display:block;font-size:10px;font-weight:600;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:6px}
  .field-input{width:100%;padding:10px 13px;background:rgba(59,158,255,0.04);border:1px solid var(--border);border-radius:10px;color:var(--text);font-family:'Outfit',sans-serif;font-size:14px;outline:none;transition:border-color 0.2s,box-shadow 0.2s}
  .field-input:focus{border-color:var(--ai-blue);background:rgba(59,158,255,0.07);box-shadow:0 0 0 3px rgba(59,158,255,0.1)}
  .field-input::placeholder{color:rgba(90,122,153,0.5)}

  .vat-generate-row{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .vat-generate-row .btn{width:auto}
  .generate-hint{font-size:12px;color:var(--muted);font-style:italic}

  .btn-primary{background:linear-gradient(135deg,var(--ai-deep),var(--ai-blue));color:#fff;box-shadow:0 4px 20px rgba(59,158,255,0.3)}
  .btn-primary:hover:not(:disabled){box-shadow:0 8px 28px rgba(59,158,255,0.4)}
  .btn-secondary{background:rgba(255,255,255,0.05);color:var(--muted-light);border:1px solid var(--border)}
  .btn-secondary:hover:not(:disabled){background:rgba(255,255,255,0.09);color:var(--text)}

  .vat-error-banner{background:rgba(255,107,138,0.08);border:1px solid rgba(255,107,138,0.25);color:var(--error);border-radius:8px}
  .vat-success-banner{background:rgba(34,211,168,0.08);border:1px solid rgba(34,211,168,0.25);color:var(--success);border-radius:8px}

  .vat-legend{background:var(--panel);border:1px solid var(--border);border-radius:14px;padding:16px 18px;font-size:12px;color:var(--muted-light)}
  .vat-legend-title{font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:var(--ai-bright);margin-bottom:10px}
  .vat-legend-row{padding:6px 0;border-bottom:1px solid var(--border);line-height:1.5}
  .vat-legend-row:last-child{border-bottom:none}
  .vat-legend-row strong{color:var(--text)}

  /* Overview grouped sections */
  .ov-section { margin-bottom:28px; animation:fadeUp 0.5s ease both; }
  .ov-section:nth-child(1){animation-delay:.05s}.ov-section:nth-child(2){animation-delay:.12s}.ov-section:nth-child(3){animation-delay:.18s}
  .ov-section-hd { display:flex; align-items:center; gap:14px; margin-bottom:14px; }
  .ov-section-icon { font-size:19px; }
  .ov-section-title { font-size:14px; font-weight:700; color:var(--muted-light); letter-spacing:.08em; text-transform:uppercase; white-space:nowrap; }
  .ov-section-line { flex:1; height:1px; background:var(--border); }
  .ov-sub-grid { display:flex; flex-wrap:wrap; justify-content:center; gap:16px; }
  .ov-sub-card { background:var(--panel); border:1px solid var(--border); border-radius:16px; padding:24px 22px; cursor:pointer; transition:all 0.2s; display:flex; flex-direction:column; gap:11px; flex:1 1 250px; max-width:300px; min-height:170px; }
  .ov-sub-card:hover { border-color:var(--border-blue); background:rgba(59,158,255,0.04); transform:translateY(-2px); box-shadow:0 6px 24px rgba(59,158,255,0.1); }
  .ov-sub-icon { font-size:30px; }
  .ov-sub-name { font-size:15px; font-weight:700; color:var(--text); }
  .ov-sub-desc { font-size:12.5px; color:var(--muted); line-height:1.5; flex:1; }
  .ov-sub-arrow { font-size:12px; color:var(--ai-blue); font-weight:600; }
  /* Sidebar accordion */
  .nav-sub-list { overflow:hidden; max-height:0; transition:max-height 0.28s cubic-bezier(0.4,0,0.2,1); }
  .nav-sub-list.open { max-height:240px; }

  /* Auto-hide sidebar (desktop) — hidden by default, slides in on hover near the
     left edge or while the cursor stays over it, slides back out when the cursor
     leaves. Mobile/tablet keeps the existing hamburger-driven drawer below. */
  .sidebar-hover-zone { display:none; position:fixed; top:0; left:0; bottom:0; width:14px; z-index:99; }
  /* Auto-hide only applies on pages carrying the .sidebar-autohide class (Overview).
     Everywhere else the sidebar stays put using the default static rules above. */
  @media(min-width:901px){
    .app.sidebar-autohide .main{margin-left:0}
    .app.sidebar-autohide .sidebar{transform:translateX(-100%); box-shadow:none;}
    .app.sidebar-autohide .sidebar:hover{transform:translateX(0); box-shadow:10px 0 36px rgba(0,0,0,0.45);}
    .app.sidebar-autohide .sidebar-hover-zone{display:block}
    .app.sidebar-autohide .sidebar-hover-zone:hover + .sidebar{transform:translateX(0); box-shadow:10px 0 36px rgba(0,0,0,0.45);}
  }

  @media(max-width:900px){
    .sidebar{transform:translateX(-100%)}.sidebar.open{transform:translateX(0)}
    .sidebar-overlay.open{display:block}.main{margin-left:0}.hamburger{display:block}
    .stats-grid{grid-template-columns:repeat(2,1fr)}.two-col{grid-template-columns:1fr}
    .ov-sub-card{flex:1 1 calc(50% - 8px); max-width:none;}
    .inv-table th:nth-child(3),.inv-table td:nth-child(3),
    .inv-table th:nth-child(5),.inv-table td:nth-child(5){display:none}
    .page{padding:16px}
  }
  @media(max-width:480px){
    .stats-grid{grid-template-columns:1fr 1fr;gap:10px}.stat-value{font-size:18px}
    .topbar{padding:10px 14px}.page{padding:12px}.ov-sub-card{flex:1 1 100%; max-width:none;}
    .inv-table th:nth-child(2),.inv-table td:nth-child(2){display:none}
  }
`;

/* ─── helpers ─── */
const fmt = n => `E ${Number(n).toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const confBadge = c => {
  const m={HIGH:"badge-high",MEDIUM:"badge-medium",LOW:"badge-low"};
  return <span className={`badge ${m[c]||"badge-low"}`}><span className="badge-dot"/>{c}</span>;
};

function CircProgress({ pct, stage }) {
  const r=52, circ=2*Math.PI*r, offset=circ-(pct/100)*circ;
  return (
    <div className="circ-wrap">
      <div className="circ-container">
        <svg width="120" height="120" className="circ-svg">
          <defs><linearGradient id="blueGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#1a6fd4"/><stop offset="100%" stopColor="#60b8ff"/>
          </linearGradient></defs>
          <circle className="circ-track" cx="60" cy="60" r={r} strokeWidth="8"/>
          <circle className="circ-fill" cx="60" cy="60" r={r} strokeWidth="8" strokeDasharray={circ} strokeDashoffset={offset}/>
        </svg>
        <div className="circ-center"><span className="circ-pct">{pct}</span><span className="circ-unit">%</span></div>
      </div>
      <div className="circ-stage">{stage}</div>
    </div>
  );
}

/* ─── nav definitions ─── */
const VAT_SUBS  = [
  {id:"purchases", icon:"📄", label:"Purchases"},
  {id:"sales",     icon:"🛒", label:"Sales"},
  {id:"vatreturn", icon:"📈", label:"VAT Return"},
];
const BANK_SUBS = [{id:"current",icon:"💳",label:"Current Account"},{id:"loan",icon:"📋",label:"Loan Account"},{id:"merge",icon:"🔗",label:"Merge Statements"}];
const REPORTS_SUBS = [
  {id:"qb",      icon:"📦", label:"QB Reports"},
  {id:"zreport", icon:"🧾", label:"Z - Slips"},
];
const CONNECTORS_SUBS = [
  {id:"xero",       letter:"X", color:"#13B5EA", label:"Xero"},
  {id:"quickbooks", letter:"Q", color:"#2CA01C", label:"QuickBooks"},
  {id:"sage",       letter:"S", color:"#00A88E", label:"Sage"},
  {id:"zoho",       letter:"Z", color:"#E42527", label:"Zoho Books"},
];

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function ClientDashboard({ onLogout, isAdminView, onBackToAdmin }) {
  /* nav */
  const [mainTab, setMainTab] = useState("overview");
  const [vatSub,  setVatSub]  = useState("purchases");
  const [bankSub, setBankSub] = useState("current");
  const [reportsSub, setReportsSub] = useState("qb");
  const [connectorsSub, setConnectorsSub] = useState("xero");
  const [sideOpen, setSide]   = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(() => new Set(["vat","bank","reports","connectors"]));
  const [selectedClientId, setSelectedClientId] = useState(null);

  /* ── Connector OAuth return: after connecting Xero/QuickBooks/Sage/Zoho,
     the browser does a full-page round trip to the provider and back —
     api_connectors_endpoints.py's callback redirects to either
     "/?connected={provider}&client_id={id}" (a per-client connection made
     from ClientProfile.jsx) or just "/?connected={provider}" (a firm-wide
     connection made from this page's own Connect Account section — no
     client_id at all, since it isn't tied to one). A full navigation
     resets all React state, so on mount we check for those params and
     land back in the right place either way, then strip the params so a
     later refresh doesn't re-trigger this. ──────────────────────────── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    if (!connected) return;
    const clientIdFromUrl = params.get("client_id");
    if (clientIdFromUrl) {
      // per-client connection — reopen that client's profile
      setSelectedClientId(clientIdFromUrl);
      setMainTab(null);
    } else {
      // firm-wide connection — land back on that provider's Connect Account page
      setConnectorsSub(connected);
      setMainTab("connectors");
      setSidebarExpanded(prev => { const s = new Set(prev); s.add("connectors"); return s; });
    }
    params.delete("connected");
    params.delete("client_id");
    const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
    window.history.replaceState({}, "", cleanUrl);
  }, []);

  /* ── Auto-logout: 20-min inactivity (also catches closed tabs / laptop sleep) */
  useInactivityLogout(onLogout);

  /* ── Auto-logout: 401 from any API call (expired / missing token) ─────── */
  useEffect(() => {
    const handleExpired = () => {
      clearActivityTimestamp();
      onLogout?.();
    };
    window.addEventListener("vatica:session-expired", handleExpired);
    return () => window.removeEventListener("vatica:session-expired", handleExpired);
  }, [onLogout]);

  /* processing status — drives sidebar live indicators */
  const { processingKeys } = useProcessingStatus();
  const isBankBusy = processingKeys.has("bank");

  /* user */
  const storedUsername = localStorage.getItem("username") || "Client User";
  const initials = storedUsername.split(/[\s._@-]/).filter(Boolean).map(w=>w[0]?.toUpperCase()||"").slice(0,2).join("")||"CU";

  /* purchases */
  const [files,setFiles]           = useState([]);
  const [dragOver,setDrag]         = useState(false);
  const [processing,setProc]       = useState(false);
  const [progress,setProg]         = useState(0);
  const [stage,setStage]           = useState("");
  const [logs,setLogs]             = useState([]);
  const [apiError,setApiError]     = useState("");
  const [invoices,setInvoices]     = useState([]);
  const [processed,setProcessed]   = useState(false);
  const [purchaseResults,setPurchaseResults] = useState([]);
  const [reviewResults,setReviewResults]     = useState([]);
  const fileRef = useRef(null);
  const logRef  = useRef(null);

  /* sales */
  const [sales,setSales]   = useState([]);
  /* AI sales extraction */
  const [salesFiles,setSalesFiles]         = useState([]);
  const [salesExtracting,setSalesExtracting] = useState(false);
  const [salesExtracted,setSalesExtracted] = useState([]);   // AI results
  const [salesDragOver,setSalesDrag]       = useState(false);
  const salesUploadRef = useRef(null);


  /* VAT import */
  const [purchFile,setPurchFile]     = useState(null);
  const [salesFile,setSalesFile]     = useState(null);
  const [impInputVAT,setIIV]         = useState(null);
  const [impOutputVAT,setIOV]        = useState(null);
  const [impStdDomestic,setISD]      = useState(null);
  const [impZeroPurch,setIZP]        = useState(null);
  const [impStdSales,setISS]         = useState(null);
  const [impZeroSales,setIZS]        = useState(null);
  const purchRef       = useRef(null);
  const salesImportRef = useRef(null);
  /* schedule generator */
  const [purchFileObj,  setPurchFileObj]  = useState(null);
  const [salesFileObj,  setSalesFileObj]  = useState(null);

  /* multi-receipt purchases */
  const [mrpFiles, setMrpFiles]         = useState([]);    // File[] — batch of PDFs merged into one combined Excel
  const [mrpDragOver, setMrpDrag]       = useState(false);
  const [mrpProcessing, setMrpProc]     = useState(false);
  const [mrpBuffer, setMrpBuffer]       = useState(null);   // ArrayBuffer — ready to download / merge
  const [mrpFilename, setMrpFilename]   = useState("");
  const mrpRef = useRef(null);

  /* multi-receipt sales */
  const [mrsFiles, setMrsFiles]         = useState([]);    // File[] — batch of PDFs merged into one combined Excel
  const [mrsDragOver, setMrsDrag]       = useState(false);
  const [mrsProcessing, setMrsProc]     = useState(false);
  const [mrsBuffer, setMrsBuffer]       = useState(null);   // ArrayBuffer
  const [mrsFilename, setMrsFilename]   = useState("");
  const mrsRef = useRef(null);

  /* quickbooks report */
  const [qbFile, setQbFile]             = useState(null);
  const [qbDragOver, setQbDrag]         = useState(false);
  const [qbProcessing, setQbProc]       = useState(false);
  const [qbResult, setQbResult]         = useState(null);   // {outputs_b64, inputs_b64, filename_outputs, filename_inputs}
  const [qbError, setQbError]           = useState("");
  const qbRef = useRef(null);

  /* z-reports */
  const [zrClient, setZrClient]         = useState(null);
  const [zrFile, setZrFile]             = useState(null);
  const [zrDragOver, setZrDrag]         = useState(false);
  const [zrProcessing, setZrProc]       = useState(false);
  const [zrBuffer, setZrBuffer]         = useState(null);   // ArrayBuffer
  const [zrFilename, setZrFilename]     = useState("");
  const [zrError, setZrError]           = useState("");
  const zrRef = useRef(null);
  const zrMergeRef = useRef(null);
  /* optional — one TIN + one Customer name applied to every row in the
     batch. Purely optional: leaving these blank does not block or change
     extraction, the user can always fill the Excel columns by hand later. */
  const [zrTin, setZrTin]               = useState("");
  const [zrCustomer, setZrCustomer]     = useState("");
  /* z-report merge — combine the just-processed workbook with one or more
     previously downloaded Z-Report Excel files (e.g. earlier batches this
     month). Supports selecting multiple files, merged in one pass. */
  const [zrMergeFiles, setZrMergeFiles] = useState([]);   // File[]
  const [zrMerging, setZrMerging]       = useState(false);

  /* merge state */
  const [mergingPurch, setMergingPurch] = useState(false);
  const [mergingSales, setMergingSales] = useState(false);

  /* client selector */
  const [currentReportId,setCurrentReportId]     = useState(null);
  const [vatClient, setVatClient]   = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [clientList, setClientList] = useState([]);
  useEffect(() => {
    getMyClients().then(d => setClientList(d || [])).catch(() => {});
  }, []);

  /* derived */
  const inputVAT    = impInputVAT  ?? invoices.reduce((s,i)=>s+i.vat,0);
  const outputVAT   = impOutputVAT ?? sales.reduce((s,r)=>s+r.vat,0);
  const netPayable  = outputVAT - inputVAT;
  const highCount   = invoices.filter(i=>i.conf==="HIGH").length;
  const lowCount    = invoices.filter(i=>i.conf==="LOW").length;
  const totalSalesVAT  = sales.reduce((s,r)=>s+r.vat,0);
  const totalSalesIncl = sales.reduce((s,r)=>s+r.total,0);
  const stdSales    = impStdSales    ?? sales.filter(r=> r.cat.includes("Standard")).reduce((s,r)=>s+r.excl,0);
  const zeroSales   = impZeroSales   ?? sales.filter(r=>!r.cat.includes("Standard")).reduce((s,r)=>s+r.excl,0);
  const stdDomestic = impStdDomestic ?? invoices.reduce((s,i)=>s+i.subtotal,0);
  const zeroPurch   = impZeroPurch   ?? invoices.reduce((s,i)=>s+(i.zero_exempt_amount||0),0);

  /* nav helpers */
  const goTo = (main,sub) => {
    setMainTab(main);
    if(sub && main==="vat")     setVatSub(sub);
    if(sub && main==="bank")    setBankSub(sub);
    if(sub && main==="reports") setReportsSub(sub);
    if(sub && main==="connectors") setConnectorsSub(sub);
    setSelectedClientId(null);
    setSide(false);
    if(main==="vat"||main==="bank"||main==="reports"||main==="connectors"){
      setSidebarExpanded(prev=>{ const s=new Set(prev); s.add(main); return s; });
    }
  };
  const topbarLabel = () => {
    if(selectedClientId) return "Client Profile";
    if(mainTab==="vat")     { const s=VAT_SUBS.find(x=>x.id===vatSub);         return s?`VAT › ${s.label}`:"VAT"; }
    if(mainTab==="bank")    { const s=BANK_SUBS.find(x=>x.id===bankSub);       return s?`Bank Statements › ${s.label}`:"Bank Statements"; }
    if(mainTab==="reports") { const s=REPORTS_SUBS.find(x=>x.id===reportsSub); return s?`Reports › ${s.label}`:"Reports"; }
    if(mainTab==="connectors") { const s=CONNECTORS_SUBS.find(x=>x.id===connectorsSub); return s?`Connect Account › ${s.label}`:"Connect Account"; }
    return {overview:"Overview",clients:"Clients",usage:"Page Count",connectors:"Connect Account"}[mainTab]||"";
  };
  /* Simple page name — shown centered in the topbar on non-Overview pages */
  const pageTitle = () => {
    if(selectedClientId) return "Client Profile";
    if(mainTab==="vat")     { const s=VAT_SUBS.find(x=>x.id===vatSub);         return s?.label||"VAT"; }
    if(mainTab==="bank")    { const s=BANK_SUBS.find(x=>x.id===bankSub);       return s?.label||"Bank Statements"; }
    if(mainTab==="reports") { const s=REPORTS_SUBS.find(x=>x.id===reportsSub); return s?.label||"Reports"; }
    if(mainTab==="connectors") { const s=CONNECTORS_SUBS.find(x=>x.id===connectorsSub); return s?.label||"Connect Account"; }
    return {clients:"Clients",usage:"Page Count",connectors:"Connect Account"}[mainTab]||"";
  };

  /* log */
  const addLog = (msg,type="") => {
    setLogs(l=>[...l,{msg,type}]);
    setTimeout(()=>{if(logRef.current)logRef.current.scrollTop=logRef.current.scrollHeight;},50);
  };

  /* files */
  const handleFiles = f => {
    const valid=Array.from(f).filter(x=>["application/pdf","image/jpeg","image/png","image/jpg"].includes(x.type));
    setFiles(p=>[...p,...valid]);
  };
  const handleDrop = e=>{e.preventDefault();setDrag(false);handleFiles(e.dataTransfer.files);};
  const removeFile = i=>setFiles(f=>f.filter((_,j)=>j!==i));

  /* process invoices */
  const startProcessing = async()=>{
    if(!files.length) return;
    setProc(true);setLogs([]);setProg(0);setStage("");setApiError("");

    // Log each file being submitted
    addLog(`📤 Uploading ${files.length} invoice${files.length!==1?"s":""}...`);
    files.forEach(f=>addLog(`   📎 ${f.name}  (${(f.size/1024).toFixed(0)} KB)`));
    setProg(10);setStage("📤 Uploading invoices...");

    // Animate per-file extraction progress while the API call is in flight
    let _fIdx=0;
    const _interval=setInterval(()=>{
      if(_fIdx<files.length){
        setProg(10+Math.round(((_fIdx+1)/files.length)*55));
        setStage(`🔍 Extracting ${_fIdx+1} / ${files.length}…`);
        addLog(`🔍 Extracting: ${files[_fIdx].name}`);
        _fIdx++;
      }
    }, Math.max(900, Math.min(3000, 12000/files.length)));

    try{
      const response = await processInvoices(files);
      clearInterval(_interval);

      setProg(88);setStage("✓ Validating results…");
      addLog("─────────────────────────────");
      addLog("✓ Extraction complete — results:");

      const mapped = response.invoices.map((inv,idx)=>({
        id:inv.id||idx+1,supplier:inv.supplier,inv_no:inv.inv_no,date:inv.date,
        subtotal:inv.subtotal,vat:inv.vat,total:inv.total,conf:inv.conf,
      }));

      // Per-invoice result lines
      mapped.forEach((inv,i)=>{
        const icon=inv.conf==="HIGH"?"✅":inv.conf==="MEDIUM"?"⚠":"🔴";
        const t=inv.conf==="HIGH"?"ok":inv.conf==="LOW"?"err":"";
        addLog(`${icon} [${i+1}] ${inv.supplier||"Unknown"} · ${inv.inv_no||"—"} · E${Number(inv.total).toLocaleString("en",{minimumFractionDigits:2})} [${inv.conf}]`,t);
      });

      setInvoices(mapped);
      setPurchaseResults(response.purchase_results||[]);
      setReviewResults(response.review_results||[]);
      setProcessed(true);
      if(response.report_id){
        setCurrentReportId(response.report_id);
        // Save the Excel to history right away — don't wait for the user
        // to click "Download Excel". This is what makes the report show
        // up on the admin side the moment extraction finishes.
        try{
          const autoBuf = await generateVatExcel(response.purchase_results||[], response.review_results||[], vatClient?.name||"", "", true);
          if(autoBuf){
            const safeClient = vatClient?.name ? `_${vatClient.name.replace(/\s+/g,'_')}` : '';
            const today = new Date().toISOString().split('T')[0];
            await saveExcelToHistory(response.report_id, autoBuf, `VAT_Purchases_Report${safeClient}_${today}.xlsx`);
            if(vatClient?.id) await tagReportToClient(response.report_id, vatClient.id);
            addLog("✓ Saved to report history","ok");
          }
        }catch(e){ addLog(`⚠ Could not auto-save to history: ${e.message}`, ""); }
      }
      setProg(100);setStage("✓ Processing complete");
      addLog("─────────────────────────────");
      addLog(`✓ ${mapped.length} invoice(s) processed`,  "ok");
      addLog(`✓ High confidence: ${mapped.filter(i=>i.conf==="HIGH").length}`, "ok");
      addLog(`⚠ Needs review: ${mapped.filter(i=>i.conf==="LOW").length}`);
      setFiles([]);
    }catch(err){
      clearInterval(_interval);
      setApiError(err.message||"Processing failed.");
      addLog(`✗ ${err.message||"Processing failed."}`, "err");
      setStage("❌ Error occurred");setProg(0);
    }finally{setProc(false);}
  };

  const clearSales=()=>{ setConfirmModal({ title:"Delete All Sales?", message:"All recorded sales for this session will be cleared. This cannot be undone.", confirmLabel:"Delete All", onConfirm:()=>setSales([]) }); };

  /* VAT import */
  const handlePurchImport=async e=>{
    const f=e.target.files[0];if(!f)return;
    setPurchFileObj(f);
    try{setPurchFile(f.name);const r=await parseVatExcel(f,"purchases");setIIV(r.input_vat);setISD(r.standard_rated_domestic);setIZP(r.zero_exempt_total||0);addLog(`✓ Purchases imported: Input VAT E${r.input_vat.toFixed(2)}`,"ok");}
    catch(err){setPurchFile(null);addLog(`✗ ${err.message}`,"err");}
  };
  const handleSalesImport=async e=>{
    const f=e.target.files[0];if(!f)return;
    setSalesFileObj(f);
    try{setSalesFile(f.name);const r=await parseVatExcel(f,"sales");setIOV(r.output_vat);setISS(r.standard_rated_supplies);setIZS(r.zero_rated_supplies);addLog(`✓ Sales imported: Output VAT E${r.output_vat.toFixed(2)}`,"ok");}
    catch(err){setSalesFile(null);addLog(`✗ ${err.message}`,"err");}
  };
  const resetImports=()=>{setPurchFile(null);setSalesFile(null);setIIV(null);setIOV(null);setISD(null);setIZP(null);setISS(null);setIZS(null);setPurchFileObj(null);setSalesFileObj(null);};

  /* Excel download */
  const handleDownloadExcel=async()=>{
    try{
      if(!purchaseResults.length||!reviewResults.length) throw new Error("No processed data. Process invoices first.");
      addLog("⚙ Generating Excel...");
      const safeClient = vatClient?.name ? `_${vatClient.name.replace(/\s+/g,'_')}` : '';
      const today = new Date().toISOString().split('T')[0];
      const vatFilename = `VAT_Purchases_Report${safeClient}_${today}.xlsx`;
      const buffer=await generateVatExcel(purchaseResults,reviewResults,vatClient?.name||"");
      addLog("✓ Excel downloaded","ok");
      if(currentReportId&&buffer){
        try{
          await saveExcelToHistory(currentReportId,buffer,vatFilename);
          addLog("✓ Saved to history","ok");
          if(vatClient?.id){
            await tagReportToClient(currentReportId,vatClient.id);
            addLog(`✓ Report linked to ${vatClient.name}`,"ok");
          }
        }
        catch(e){addLog(`⚠ History save: ${e.message}`,"err");}
      }
    }catch(err){addLog(`✗ ${err.message}`,"err");}
  };

  /* ─── ClientSelector ─── */
  const ClientSelector = ({ value, onChange }) => (
    <div className="field" style={{marginBottom:16,background:"rgba(59,158,255,0.04)",border:"1px solid rgba(59,158,255,0.15)",borderRadius:12,padding:"12px 14px"}}>
      <label style={{color:"var(--ai-blue)"}}>Client</label>
      <select value={value?.id||""} onChange={e=>{
        const c = clientList.find(x => String(x.id) === e.target.value);
        onChange(c ? {id:c.id, name:c.name} : null);
      }}>
        <option value="">— Select client (optional) —</option>
        {clientList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {value && <div style={{fontSize:11,color:"var(--success)",marginTop:6}}>✓ Reports will be saved to <strong>{value.name}</strong></div>}
    </div>
  );

  /* ─── shared download helper ─── */
  const triggerDownload = (buffer, filename) => {
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href     = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  /* ─── multi-receipt purchases ─── */
  const handleMrpProcess = async () => {
    if (!mrpFiles.length) return;
    setMrpProc(true); setMrpBuffer(null);
    const totalKb = (mrpFiles.reduce((s,f)=>s+f.size,0)/1024).toFixed(0);
    addLog(`🧾 Multi-receipt purchases: ${mrpFiles.length} file(s), ${totalKb} KB total`);
    try {
      const buf = await processMultiReceiptsPurchases(mrpFiles, vatClient?.name || "", vatClient?.id || null);
      const today = new Date().toISOString().split("T")[0];
      const safeC = vatClient?.name ? `_${vatClient.name.replace(/\s+/g,"_")}` : "";
      const fname = `MultiReceipt_Purchases${safeC}_${today}.xlsx`;
      setMrpBuffer(buf);
      setMrpFilename(fname);
      addLog(`✓ Multi-receipt extraction done — ready to download or merge`, "ok");
    } catch(err) {
      addLog(`✗ ${err.message}`, "err");
    } finally { setMrpProc(false); }
  };

  /* ─── multi-receipt sales ─── */
  const handleMrsProcess = async () => {
    if (!mrsFiles.length) return;
    setMrsProc(true); setMrsBuffer(null);
    const totalKb = (mrsFiles.reduce((s,f)=>s+f.size,0)/1024).toFixed(0);
    addLog(`🧾 Multi-receipt sales: ${mrsFiles.length} file(s), ${totalKb} KB total`);
    try {
      const buf = await processMultiReceiptsSales(mrsFiles, vatClient?.name || "", vatClient?.id || null);
      const today = new Date().toISOString().split("T")[0];
      const safeC = vatClient?.name ? `_${vatClient.name.replace(/\s+/g,"_")}` : "";
      const fname = `MultiReceipt_Sales${safeC}_${today}.xlsx`;
      setMrsBuffer(buf);
      setMrsFilename(fname);
      addLog(`✓ Multi-receipt sales extraction done — ready to download or merge`, "ok");
    } catch(err) {
      addLog(`✗ ${err.message}`, "err");
    } finally { setMrsProc(false); }
  };

  /* ─── merge purchases ─── */
  const handleMergePurchases = async () => {
    if (!purchaseResults.length || !mrpBuffer) return;
    setMergingPurch(true);
    addLog("⊕ Merging purchases workbooks…");
    try {
      const standardBuf = await generateVatExcel(purchaseResults, reviewResults, vatClient?.name || "", "", true);
      const b1 = new Blob([standardBuf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const b2 = new Blob([mrpBuffer],   { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const today   = new Date().toISOString().split("T")[0];
      const safeC   = vatClient?.name ? `_${vatClient.name.replace(/\s+/g,"_")}` : "";
      const outName = `Purchases_Merged${safeC}_${today}.xlsx`;
      const merged  = await mergeExcelFiles(b1, b2, outName);
      triggerDownload(merged, outName);
      addLog(`✓ Merged Excel downloaded — ${outName}`, "ok");
    } catch(err) {
      addLog(`✗ Merge failed: ${err.message}`, "err");
    } finally { setMergingPurch(false); }
  };

  /* ─── merge sales ─── */
  const handleMergeSales = async (salesData) => {
    if (!salesData.length || !mrsBuffer) return;
    setMergingSales(true);
    addLog("⊕ Merging sales workbooks…");
    try {
      const standardBuf = await generateSalesExcel(salesData, vatClient?.name || "", "", true);
      const b1 = new Blob([standardBuf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const b2 = new Blob([mrsBuffer],   { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const today   = new Date().toISOString().split("T")[0];
      const safeC   = vatClient?.name ? `_${vatClient.name.replace(/\s+/g,"_")}` : "";
      const outName = `Sales_Merged${safeC}_${today}.xlsx`;
      const merged  = await mergeExcelFiles(b1, b2, outName);
      triggerDownload(merged, outName);
      addLog(`✓ Merged Sales Excel downloaded — ${outName}`, "ok");
    } catch(err) {
      addLog(`✗ Merge failed: ${err.message}`, "err");
    } finally { setMergingSales(false); }
  };

  /* ─── z-reports ─── */
  const handleZrProcess = async () => {
    if (!zrFile) return;
    setZrProc(true); setZrBuffer(null); setZrError("");
    addLog(`🧾 Z-Report: ${zrFile.name}  (${(zrFile.size/1024).toFixed(0)} KB)`);
    try {
      const buf = await processZReports(
        zrFile, zrClient?.name || "", zrClient?.id || null,
        zrTin.trim(), zrCustomer.trim()
      );
      const today = new Date().toISOString().split("T")[0];
      const safeC = zrClient?.name ? `_${zrClient.name.replace(/\s+/g,"_")}` : "";
      const fname = `Z_Reports${safeC}_${today}.xlsx`;
      setZrBuffer(buf);
      setZrFilename(fname);
      addLog(`✓ Z-Report extraction done — ready to download`, "ok");
    } catch(err) {
      setZrError(err.message || "Processing failed");
      addLog(`✗ ${err.message}`, "err");
    } finally { setZrProc(false); }
  };

  /* ─── merge z-reports — combine this batch with one or more previously
         downloaded Z-Report Excel files (e.g. earlier days'/weeks' batches).
         Files are folded in one at a time so any number can be combined in
         a single pass; the backend re-sorts every combined row oldest-first
         by date and rebuilds the Grand Total after each fold. ─── */
  const handleZrMerge = async () => {
    if (!zrBuffer || !zrMergeFiles.length) return;
    setZrMerging(true);
    addLog(`⊕ Merging Z-Report workbook${zrMergeFiles.length>1?"s":""} (${zrMergeFiles.length} file${zrMergeFiles.length>1?"s":""})…`);
    try {
      let currentBuf  = zrBuffer;
      const today     = new Date().toISOString().split("T")[0];
      const safeC     = zrClient?.name ? `_${zrClient.name.replace(/\s+/g,"_")}` : "";
      const outName   = `Z_Reports_Merged${safeC}_${today}.xlsx`;

      for (const f of zrMergeFiles) {
        const b1 = new Blob([currentBuf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        currentBuf = await mergeExcelFiles(b1, f, outName);
      }

      triggerDownload(currentBuf, outName);
      addLog(`✓ Merged Z-Report Excel downloaded — ${outName} (sorted oldest → newest)`, "ok");
    } catch(err) {
      addLog(`✗ Merge failed: ${err.message}`, "err");
    } finally { setZrMerging(false); }
  };

  /* ═══ RENDERERS ═══ */

  const renderOverview=()=>(
    <div className="page">

      {/* ── VAT Automation section ── */}
      <div className="ov-section">
        <div className="ov-section-hd">
          <span className="ov-section-line"/>
          <span className="ov-section-icon">📊</span>
          <span className="ov-section-title">VAT Automation</span>
          <span className="ov-section-line"/>
        </div>
        <div className="ov-sub-grid">
          {[
            {icon:"🧾",name:"Purchases",desc:"Upload supplier invoices and extract input VAT automatically.",arrow:"Open →",action:()=>goTo("vat","purchases")},
            {icon:"💰",name:"Sales",desc:"Record sales invoices and compute output VAT for the period.",arrow:"Open →",action:()=>goTo("vat","sales")},
            {icon:"📋",name:"VAT Return",desc:"Generate ERS VT.01 return PDF and Excel schedules for submission.",arrow:"Open →",action:()=>goTo("vat","vatreturn")},
            {icon:"📁",name:"Reports",desc:"Import QB Reports & Z - Slips and convert to ERS format.",arrow:"Open →",action:()=>goTo("reports","qb")},
          ].map((c,i)=>(
            <div key={i} className="ov-sub-card" onClick={c.action}>
              <div className="ov-sub-icon">{c.icon}</div>
              <div className="ov-sub-name">{c.name}</div>
              <div className="ov-sub-desc">{c.desc}</div>
              <div className="ov-sub-arrow">{c.arrow}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bank Statements section ── */}
      <div className="ov-section">
        <div className="ov-section-hd">
          <span className="ov-section-line"/>
          <span className="ov-section-icon">🏦</span>
          <span className="ov-section-title">Bank Statements</span>
          <span className="ov-section-line"/>
        </div>
        <div className="ov-sub-grid">
          {[
            {icon:"💳",name:"Current Account",desc:"Extract transactions from current account statements and categorise cashflow.",arrow:"Open →",action:()=>goTo("bank","current")},
            {icon:"📋",name:"Loan Account",desc:"Extract loan statement data and analyse repayment schedules.",arrow:"Open →",action:()=>goTo("bank","loan")},
            {icon:"🔗",name:"Merge Statements",desc:"Combine 2+ extracted statements — any bank, current or loan — into one consolidated working paper.",arrow:"Open →",action:()=>goTo("bank","merge")},
          ].map((c,i)=>(
            <div key={i} className="ov-sub-card" onClick={c.action}>
              <div className="ov-sub-icon">{c.icon}</div>
              <div className="ov-sub-name">{c.name}</div>
              <div className="ov-sub-desc">{c.desc}</div>
              <div className="ov-sub-arrow">{c.arrow}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Management section ── */}
      <div className="ov-section">
        <div className="ov-section-hd">
          <span className="ov-section-line"/>
          <span className="ov-section-icon">⚙️</span>
          <span className="ov-section-title">Management</span>
          <span className="ov-section-line"/>
        </div>
        <div className="ov-sub-grid">
          {[
            {icon:"👥",name:"Clients",desc:"Manage your client portfolio and view per-client report history.",arrow:"Open →",action:()=>goTo("clients")},
            {icon:"📄",name:"Page Count",desc:"View pages processed this billing period across all extractors.",arrow:"Open →",action:()=>goTo("usage")},
          ].map((c,i)=>(
            <div key={i} className="ov-sub-card" onClick={c.action}>
              <div className="ov-sub-icon">{c.icon}</div>
              <div className="ov-sub-name">{c.name}</div>
              <div className="ov-sub-desc">{c.desc}</div>
              <div className="ov-sub-arrow">{c.arrow}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );

  const renderConnectors=()=>(
    <div className="page">
      {/* clientId={null} = the tax agent's own account, not tied to any one
         client. providerFilter scopes this page to just the one provider
         picked from the sidebar/Overview card. */}
      {/* key forces a full remount on every provider switch — otherwise
         React reuses the same component instance across Sage/QuickBooks/
         Xero/Zoho (only providerFilter changes, not the component's
         identity), which leaves stale internal state behind: an open
         Sage form, an in-progress pull, an old error message, etc. all
         bleeding into whichever provider you switch to next. */}
      <ConnectedAccounts key={connectorsSub} clientId={null} providerFilter={connectorsSub} showHeader={true} />
    </div>
  );

  const renderPurchases=()=>(
    <div className="page">

      {/* ── Client selector — above both panels ── */}
      <ClientSelector value={vatClient} onChange={setVatClient}/>

      {/* ── Two upload panels side by side ── */}
      <div className="two-col" style={{marginBottom:16}}>

          {/* Panel A — Standard Invoices (existing flow) */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">📤 One Invoice Per Page</span>
              {processed&&<span style={{fontSize:11,color:"var(--success)"}}>✓ {invoices.length} processed</span>}
            </div>
            <div className="panel-body">
              {!processing&&!processed?(
                <>
                  <div className={`upload-zone ${dragOver?"drag-over":""}`} onDrop={handleDrop} onDragOver={e=>{e.preventDefault();setDrag(true);}} onDragLeave={()=>setDrag(false)} onClick={()=>fileRef.current?.click()}>
                    <div className="upload-icon">📤</div>
                    <div className="upload-title">Drop invoices here</div>
                    <div className="upload-sub">PDF, PNG, JPG — <span>click to browse</span></div>
                  </div>
                  <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" multiple style={{display:"none"}} onChange={e=>handleFiles(e.target.files)}/>
                  {files.length>0&&(
                    <div style={{marginTop:14}}>
                      {files.map((f,i)=><div className="file-item" key={i}><span className="file-name">{f.name}</span><span className="file-size">{(f.size/1024).toFixed(0)} KB</span><button className="file-remove" onClick={()=>removeFile(i)}>✕</button></div>)}
                      <button className="btn btn-blue" style={{marginTop:8}} onClick={startProcessing}>🚀 Process {files.length} Invoice{files.length!==1?"s":""}</button>
                    </div>
                  )}
                </>
              ):processing?(
                <><CircProgress pct={progress} stage={stage}/><div ref={logRef} className="log-box">{logs.map((l,i)=><div key={i} className={l.type?`log-${l.type}`:""}>{">"} {l.msg}</div>)}</div></>
              ):(
                <>
                  <div style={{textAlign:"center",padding:"16px 0 8px"}}>
                    <div style={{fontSize:32,marginBottom:6}}>✅</div>
                    <div style={{fontWeight:700,color:"var(--success)",marginBottom:4}}>{invoices.length} Invoice{invoices.length!==1?"s":""} Processed</div>
                    <div style={{fontSize:12,color:"var(--muted)"}}>High: {highCount} · Needs review: {lowCount}</div>
                  </div>
                  <div ref={logRef} className="log-box" style={{height:70}}>{logs.map((l,i)=><div key={i} className={l.type?`log-${l.type}`:""}>{">"} {l.msg}</div>)}</div>
                  {apiError&&<div style={{marginTop:8,fontSize:12,color:"var(--error)"}}>⚠ {apiError}</div>}
                  <div style={{marginTop:12,display:"flex",gap:8}}>
                    <button className="btn btn-blue" onClick={handleDownloadExcel} disabled={!purchaseResults.length}>⬇ Download Purchases Excel</button>
                    <button className="btn btn-ghost btn-sm" style={{width:"auto"}} onClick={()=>{setProcessed(false);setFiles([]);setInvoices([]);setPurchaseResults([]);setReviewResults([]);}}>↺ New</button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Panel B — Multi-Receipt PDF (new) */}
          <div className="panel" style={{border:"1px solid rgba(59,158,255,0.15)"}}>
            <div className="panel-header">
              <span className="panel-title">🧾 Multi-Receipt PDF</span>
              {mrpBuffer&&<span style={{fontSize:11,color:"var(--success)"}}>✓ Ready to download</span>}
            </div>
            <div className="panel-body">
              <p style={{fontSize:11,color:"var(--muted)",marginBottom:12,lineHeight:1.6}}>
                Drop one or more PDFs — each can contain <strong>multiple receipts</strong> per page, printed or handwritten. Every receipt from every file is merged into one combined Excel.
              </p>
              {!mrpProcessing&&!mrpBuffer&&(
                <>
                  <div
                    onDrop={e=>{e.preventDefault();setMrpDrag(false);const fs=Array.from(e.dataTransfer.files||[]).filter(f=>f.type==="application/pdf");if(fs.length)setMrpFiles(p=>[...p,...fs]);}}
                    onDragOver={e=>{e.preventDefault();setMrpDrag(true);}}
                    onDragLeave={()=>setMrpDrag(false)}
                    onClick={()=>mrpRef.current?.click()}
                    className={`upload-zone ${mrpDragOver?"drag-over":""}`}>
                    <div className="upload-icon">🧾</div>
                    <div className="upload-title">Drop multi-receipt PDF(s)</div>
                    <div className="upload-sub">PDF only — <span>click to browse, select multiple</span></div>
                  </div>
                  <input ref={mrpRef} type="file" accept=".pdf" multiple style={{display:"none"}} onChange={e=>{const fs=Array.from(e.target.files||[]);if(fs.length)setMrpFiles(p=>[...p,...fs]);e.target.value="";}}/>
                  {mrpFiles.length>0&&(
                    <div style={{marginTop:12}}>
                      {mrpFiles.map((f,i)=>(
                        <div key={`${f.name}-${i}`} className="file-item"><span className="file-name">{f.name}</span><span className="file-size">{(f.size/1024).toFixed(0)} KB</span><button className="file-remove" onClick={()=>setMrpFiles(p=>p.filter((_,idx)=>idx!==i))}>✕</button></div>
                      ))}
                      <button className="btn btn-teal" style={{marginTop:8}} onClick={handleMrpProcess}>🚀 Extract Multi-Receipts ({mrpFiles.length} file{mrpFiles.length>1?"s":""})</button>
                    </div>
                  )}
                </>
              )}
              {mrpProcessing&&(
                <div style={{padding:"12px 0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                    <span style={{fontSize:22}}>🧾</span>
                    <div>
                      <div style={{fontWeight:700,fontSize:13,color:"var(--ai-blue)"}}>Extracting multi-receipt PDF…</div>
                      <div style={{fontSize:11,color:"var(--muted)"}}>Processing all pages in parallel</div>
                    </div>
                  </div>
                  <div style={{background:"rgba(59,158,255,0.08)",borderRadius:99,height:4,overflow:"hidden"}}>
                    <div style={{height:"100%",background:"linear-gradient(90deg,var(--ai-deep),var(--ai-blue))",borderRadius:99,width:"100%",animation:"indeterminate 1.6s ease-in-out infinite"}}/>
                  </div>
                </div>
              )}
              {!mrpProcessing&&mrpBuffer&&(
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,background:"rgba(34,211,168,0.07)",border:"1px solid rgba(34,211,168,0.2)",borderRadius:12,padding:"10px 14px"}}>
                    <span style={{fontSize:22}}>✅</span>
                    <div style={{fontWeight:700,fontSize:13,color:"var(--success)"}}>Multi-receipt extraction complete</div>
                  </div>
                  <button className="btn btn-green" style={{marginBottom:8}} onClick={()=>triggerDownload(mrpBuffer, mrpFilename)}>⬇ Download Multi-Receipt Excel</button>
                  <button className="btn btn-ghost btn-sm" style={{marginTop:4}} onClick={()=>{setMrpBuffer(null);setMrpFiles([]);}}>↺ New PDF(s)</button>
                </div>
              )}
            </div>
          </div>

      </div>{/* end two-col upload panels */}

      {/* ── Merge strip — full width, visible when both are ready ── */}
      {processed&&purchaseResults.length>0&&mrpBuffer&&(
        <div className="panel" style={{marginBottom:16,border:"1px solid rgba(124,58,237,0.35)",background:"rgba(124,58,237,0.04)"}}>
          <div className="panel-body" style={{padding:16}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <span style={{fontSize:20}}>⊕</span>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:"var(--text)"}}>Merge Purchases Workbooks</div>
                <div style={{fontSize:11,color:"var(--muted)"}}>Combines Standard Invoices + Multi-Receipt sheets into one Excel file</div>
              </div>
            </div>
            <button className="btn btn-purple" onClick={handleMergePurchases} disabled={mergingPurch}>
              {mergingPurch?"⏳ Merging…":"⊕ Merge Both Purchases Excels"}
            </button>
          </div>
        </div>
      )}

      {/* ── Extracted invoices table — full width ── */}
      <div className="panel">
        <div className="panel-header"><span className="panel-title">Extracted Invoices</span><span style={{fontSize:12,color:"var(--muted)"}}>{invoices.length} invoice{invoices.length!==1?"s":""}</span></div>
        {invoices.length===0?<div className="empty-state"><div className="empty-icon">📄</div>Upload and process invoices to see results.</div>
          :<div style={{overflowX:"auto"}}><table className="inv-table"><thead><tr><th>#</th><th>Supplier</th><th>Inv No</th><th>Date</th><th>VAT</th><th>Total</th><th>Conf.</th></tr></thead><tbody>
            {invoices.map(i=><tr key={i.id}><td style={{color:"var(--muted)"}}>{i.id}</td><td><span className="sup-name">{i.supplier}</span></td><td><span className="inv-num">{i.inv_no}</span></td><td style={{fontSize:12}}>{i.date}</td><td><span className="inv-amt c-green">{i.vat.toFixed(2)}</span></td><td><span className="inv-amt">{i.total.toLocaleString("en",{minimumFractionDigits:2})}</span></td><td>{confBadge(i.conf)}</td></tr>)}
          </tbody></table></div>}
      </div>

    </div>
  );

  const renderSales=()=>{
    const handleSalesDrop=e=>{e.preventDefault();setSalesDrag(false);setSalesFiles(p=>[...p,...Array.from(e.dataTransfer.files).filter(f=>/\.(pdf|jpe?g|png|webp)$/i.test(f.name))]);};
    const handleSalesExtract=async()=>{
      if(!salesFiles.length)return;
      setSalesExtracting(true);setSalesExtracted([]);
      setLogs([]);
      addLog(`🧾 Starting sales extraction — ${salesFiles.length} file${salesFiles.length!==1?"s":""}...`);
      salesFiles.forEach(f=>addLog(`   📎 ${f.name}  (${(f.size/1024).toFixed(0)} KB)`));
      let _sIdx=0;
      const _sInterval=setInterval(()=>{
        if(_sIdx<salesFiles.length){addLog(`🔍 Extracting: ${salesFiles[_sIdx].name}`);_sIdx++;}
      }, Math.max(900, Math.min(3000, 12000/salesFiles.length)));
      try{
        const res=await processSalesInvoices(salesFiles,vatClient?.id||null);
        clearInterval(_sInterval);
        const ok=res.results.filter(r=>r.status==="ok");
        addLog("─────────────────────────────");
        addLog("✓ Extraction complete — results:");
        res.results.forEach(r=>{
          if(r.status==="ok"){const conf=(r.confidence||"medium").toLowerCase();const icon=conf==="high"?"✅":conf==="low"?"🔴":"⚠";const hw=r.handwriting_detected?" ✍":"";addLog(`${icon} ${r.file} · E${parseFloat(r.total_incl_vat||0).toFixed(2)} [${conf.toUpperCase()}]${hw}`,conf==="high"?"ok":conf==="low"?"err":"");}
          else{addLog(`✗ ${r.file}: ${r.error}`,"err");}
        });
        setSalesExtracted(ok);
        ok.forEach(r=>{
          const vat=parseFloat(r.vat_amount||0);const tot=parseFloat(r.total_incl_vat||0);
          const excl=Math.round((tot-vat)*100)/100;const std=vat>0?Math.round((vat/0.15)*100)/100:(r.category==="Standard"?excl:0);const zero=Math.max(0,Math.round((excl-std)*100)/100);
          setSales(p=>[...p,{id:Date.now()+Math.random(),date:r.date,cust:r.customer_name||r.vendor_name||"Unknown",vatTin:r.customer_vat_tin||"",invNo:r.invoice_number||"",desc:r.description||"Sale",cat:r.category||"Standard",qty:1,total:tot,excl,vat,std,zero,total_incl_vat:tot,vat_amount:vat,invoice_number:r.invoice_number||"",category:r.category||"Standard",vendor_name:r.vendor_name||"",vendor_vat_tin:r.vendor_vat_tin||"",customer_name:r.customer_name||"",customer_vat_tin:r.customer_vat_tin||"",confidence:r.confidence||"high",handwriting_detected:r.handwriting_detected,handwriting_ambiguous:r.handwriting_ambiguous,extraction_notes:r.extraction_notes||"",line_items:r.line_items||[],file:r.file||""}]);
        });
        addLog("─────────────────────────────");
        addLog(`✅ ${ok.length} invoice(s) extracted · ${res.failed} failed · ${res.pages_processed} page(s) · Total E${(res.total_sales||0).toFixed(2)}`,"ok");
        if(res.failed>0)addLog(`⚠ Failed: ${res.results.filter(r=>r.status==="error").map(r=>`${r.file}: ${r.error}`).join(" | ")}`,"err");
        setSalesFiles([]);
      }catch(err){clearInterval(_sInterval);addLog(`✗ ${err.message}`,"err");}
      finally{setSalesExtracting(false);}
    };
    const handleDownloadSalesExcel=async()=>{
      if(!sales.length)return;
      const safeClient=vatClient?.name?`_${vatClient.name.replace(/\s+/g,"_")}`:"";
      const today=new Date().toISOString().split("T")[0];
      const salesFilename=`Sales_Report${safeClient}_${today}.xlsx`;
      const month=new Date().toISOString().slice(0,7);
      const buffer=await generateSalesExcel(sales,vatClient?.name||"",month);
      addLog("✓ Sales Excel exported","ok");
      try{
        const snap=await saveSalesSnapshot(sales,vatClient?.id||null,vatClient?.name||"",month);
        addLog(`✓ Saved${vatClient?` and linked to ${vatClient.name}`:""}`, "ok");
        if(snap?.id){
          // Pass the snapshot's report_id so the Excel attaches to THIS
          // record instead of spawning a second, disconnected one.
          await saveSalesExcel(buffer, vatClient?.id||null, month, salesFilename, snap.id);
          addLog("✓ Sales Excel saved to server","ok");
        }
      }catch(e){addLog(`⚠ Could not save: ${e.message}`,"");}
    };
    return(
    <div className="page">

      {/* ── Client selector — above both panels ── */}
      <ClientSelector value={vatClient} onChange={setVatClient}/>

      {/* ── Two upload panels side by side ── */}
      <div className="two-col" style={{marginBottom:16}}>

          {/* Panel A — Standard Sales Invoices (existing flow) */}
          <div className="panel">
            <div className="panel-header"><span className="panel-title">📤 Upload Sales Invoices</span></div>
            <div className="panel-body">
              {!salesExtracting && !salesExtracted.length && (
                <>
                  <p style={{fontSize:11,color:"var(--muted)",marginBottom:12,lineHeight:1.6}}>Upload sales invoice PDFs (recommended) or images — printed or handwritten. The system extracts all fields automatically.</p>
                  <div
                    onDrop={handleSalesDrop}
                    onDragOver={e=>{e.preventDefault();setSalesDrag(true);}}
                    onDragLeave={()=>setSalesDrag(false)}
                    onClick={()=>salesUploadRef.current?.click()}
                    style={{border:`2px dashed ${salesDragOver?"var(--ai-blue)":"var(--border)"}`,borderRadius:12,padding:"28px 20px",textAlign:"center",cursor:"pointer",marginBottom:12,background:salesDragOver?"rgba(59,158,255,0.04)":"transparent",transition:"all 0.18s"}}>
                    <div style={{fontSize:36,marginBottom:8}}>🧾</div>
                    <div style={{fontWeight:600,fontSize:13,marginBottom:4}}>Drop sales invoices here or click to browse</div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>PDF · JPG · PNG · WEBP — printed or handwritten</div>
                  </div>
                  <input ref={salesUploadRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" style={{display:"none"}} onChange={e=>setSalesFiles(p=>[...p,...Array.from(e.target.files||[])])}/>
                  {salesFiles.length>0&&(
                    <div style={{marginBottom:12}}>
                      <div style={{fontSize:11,fontWeight:600,color:"var(--muted)",marginBottom:6}}>{salesFiles.length} file{salesFiles.length!==1?"s":""} selected</div>
                      {salesFiles.map((f,i)=>(<div key={i} className="file-item"><span className="file-name">📎 {f.name}</span><span className="file-size">{(f.size/1024).toFixed(0)} KB</span><button className="file-remove" onClick={()=>setSalesFiles(p=>p.filter((_,j)=>j!==i))}>✕</button></div>))}
                      <button className="btn btn-green" style={{marginTop:10}} onClick={handleSalesExtract}>🚀 Extract Sales Invoices</button>
                    </div>
                  )}
                </>
              )}
              {salesExtracting&&(
                <div style={{padding:"16px 0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                    <div style={{fontSize:24}}>🧾</div>
                    <div><div style={{fontWeight:700,fontSize:13,color:"var(--ai-blue)"}}>Extracting sales invoices…</div><div style={{fontSize:11,color:"var(--muted)"}}>You will receive an Excel file shortly.</div></div>
                  </div>
                  <div style={{background:"rgba(59,158,255,0.08)",borderRadius:99,height:4,overflow:"hidden",marginBottom:12}}><div style={{height:"100%",background:"linear-gradient(90deg,var(--ai-deep),var(--ai-blue))",borderRadius:99,width:"100%",animation:"indeterminate 1.6s ease-in-out infinite"}}/></div>
                  <div ref={logRef} className="log-box">{logs.map((l,i)=><div key={i} className={l.type?`log-${l.type}`:""}>{">"} {l.msg}</div>)}</div>
                </div>
              )}
              {!salesExtracting&&salesExtracted.length>0&&(
                <div style={{padding:"12px 0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,background:"rgba(34,211,168,0.07)",border:"1px solid rgba(34,211,168,0.2)",borderRadius:12,padding:"12px 16px"}}>
                    <span style={{fontSize:24}}>✅</span>
                    <div><div style={{fontWeight:700,fontSize:13,color:"var(--success)"}}>{salesExtracted.length} invoice{salesExtracted.length!==1?"s":""} extracted</div><div style={{fontSize:11,color:"var(--muted)"}}>{salesExtracted.filter(r=>r.confidence==="high").length} HIGH · {salesExtracted.filter(r=>r.confidence==="medium").length} MEDIUM · {salesExtracted.filter(r=>r.confidence==="low").length} LOW</div></div>
                  </div>
                  <button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{setSalesExtracted([]);setSalesFiles([]);}}>↺ Extract More Invoices</button>
                </div>
              )}
              <div className="divider"/>
              <p style={{fontSize:10,fontWeight:600,color:"var(--muted)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>Export</p>
              <button className="btn btn-teal" style={{marginBottom:8}} disabled={!sales.length} onClick={handleDownloadSalesExcel}>⬇ Download Sales Excel (3 sheets)</button>
              <p style={{fontSize:11,color:"var(--muted)",marginBottom:14,lineHeight:1.6}}>Downloads Sales Summary, Line Items Detail, and Review flags.</p>
              <div className="divider"/>
              <button className="btn btn-danger" onClick={clearSales} disabled={!sales.length}>🗑 Clear All Sales</button>
              {sales.length>0&&<div className="summary-lbl" style={{marginTop:10}}>Output VAT: {fmt(totalSalesVAT)}<br/>Total Sales (incl): {fmt(totalSalesIncl)}<br/>Transactions: {sales.length}</div>}
            </div>
          </div>

          {/* Panel B — Multi-Receipt Sales PDF (new) */}
          <div className="panel" style={{border:"1px solid rgba(14,165,233,0.2)"}}>
            <div className="panel-header">
              <span className="panel-title">🧾 Multi-Receipt Sales PDF</span>
              {mrsBuffer&&<span style={{fontSize:11,color:"var(--success)"}}>✓ Ready to download</span>}
            </div>
            <div className="panel-body">
              <p style={{fontSize:11,color:"var(--muted)",marginBottom:12,lineHeight:1.6}}>
                Drop one or more PDFs — each can contain <strong>multiple sales receipts</strong> per page. Every receipt from every file is merged into one combined Excel.
              </p>
              {!mrsProcessing&&!mrsBuffer&&(
                <>
                  <div
                    onDrop={e=>{e.preventDefault();setMrsDrag(false);const fs=Array.from(e.dataTransfer.files||[]).filter(f=>f.type==="application/pdf");if(fs.length)setMrsFiles(p=>[...p,...fs]);}}
                    onDragOver={e=>{e.preventDefault();setMrsDrag(true);}}
                    onDragLeave={()=>setMrsDrag(false)}
                    onClick={()=>mrsRef.current?.click()}
                    className={`upload-zone ${mrsDragOver?"drag-over":""}`}>
                    <div className="upload-icon">🧾</div>
                    <div className="upload-title">Drop multi-receipt sales PDF(s)</div>
                    <div className="upload-sub">PDF only — <span>click to browse, select multiple</span></div>
                  </div>
                  <input ref={mrsRef} type="file" accept=".pdf" multiple style={{display:"none"}} onChange={e=>{const fs=Array.from(e.target.files||[]);if(fs.length)setMrsFiles(p=>[...p,...fs]);e.target.value="";}}/>
                  {mrsFiles.length>0&&(
                    <div style={{marginTop:12}}>
                      {mrsFiles.map((f,i)=>(
                        <div key={`${f.name}-${i}`} className="file-item"><span className="file-name">{f.name}</span><span className="file-size">{(f.size/1024).toFixed(0)} KB</span><button className="file-remove" onClick={()=>setMrsFiles(p=>p.filter((_,idx)=>idx!==i))}>✕</button></div>
                      ))}
                      <button className="btn btn-teal" style={{marginTop:8}} onClick={handleMrsProcess}>🚀 Extract Multi-Receipt Sales ({mrsFiles.length} file{mrsFiles.length>1?"s":""})</button>
                    </div>
                  )}
                </>
              )}
              {mrsProcessing&&(
                <div style={{padding:"12px 0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                    <span style={{fontSize:22}}>🧾</span>
                    <div><div style={{fontWeight:700,fontSize:13,color:"var(--teal)"}}>Extracting multi-receipt sales PDF…</div><div style={{fontSize:11,color:"var(--muted)"}}>Processing all pages in parallel</div></div>
                  </div>
                  <div style={{background:"rgba(14,165,233,0.1)",borderRadius:99,height:4,overflow:"hidden"}}><div style={{height:"100%",background:"linear-gradient(90deg,#0369a1,var(--teal))",borderRadius:99,width:"100%",animation:"indeterminate 1.6s ease-in-out infinite"}}/></div>
                </div>
              )}
              {!mrsProcessing&&mrsBuffer&&(
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,background:"rgba(34,211,168,0.07)",border:"1px solid rgba(34,211,168,0.2)",borderRadius:12,padding:"10px 14px"}}>
                    <span style={{fontSize:22}}>✅</span>
                    <div style={{fontWeight:700,fontSize:13,color:"var(--success)"}}>Multi-receipt sales extraction complete</div>
                  </div>
                  <button className="btn btn-teal" style={{marginBottom:8}} onClick={()=>triggerDownload(mrsBuffer, mrsFilename)}>⬇ Download Multi-Receipt Sales Excel</button>
                  <button className="btn btn-ghost btn-sm" style={{marginTop:4}} onClick={()=>{setMrsBuffer(null);setMrsFiles([]);}}>↺ New PDF(s)</button>
                </div>
              )}
            </div>
          </div>

      </div>{/* end two-col upload panels */}

      {/* ── Merge strip — full width, visible when both are ready ── */}
      {sales.length>0&&mrsBuffer&&(
        <div className="panel" style={{marginBottom:16,border:"1px solid rgba(124,58,237,0.35)",background:"rgba(124,58,237,0.04)"}}>
          <div className="panel-body" style={{padding:16}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <span style={{fontSize:20}}>⊕</span>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:"var(--text)"}}>Merge Sales Workbooks</div>
                <div style={{fontSize:11,color:"var(--muted)"}}>Combines Standard Sales + Multi-Receipt sheets into one Excel file</div>
              </div>
            </div>
            <button className="btn btn-purple" onClick={()=>handleMergeSales(sales)} disabled={mergingSales}>
              {mergingSales?"⏳ Merging…":"⊕ Merge Both Sales Excels"}
            </button>
          </div>
        </div>
      )}

      {/* ── Extracted sales table — full width ── */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-title">Extracted Sales</span>
          <span style={{fontSize:12,color:"var(--muted)"}}>{sales.length} invoice{sales.length!==1?"s":""}</span>
        </div>
        {sales.length===0
          ?<div className="empty-state"><div className="empty-icon">🛒</div>No sales extracted yet.<br/><span style={{fontSize:11,color:"var(--muted)"}}>Upload invoices above.</span></div>
          :<div style={{overflowX:"auto"}}>
            <table className="inv-table">
              <thead><tr><th>Date</th><th>Vendor / Customer</th><th>Invoice</th><th>Category</th><th>Total</th><th>VAT</th><th>Conf</th></tr></thead>
              <tbody>
                {[...sales].reverse().map(s=>{
                  const conf=(s.confidence||"high").toLowerCase();
                  const cc=conf==="high"?"var(--success)":conf==="low"?"var(--error)":"var(--warning)";
                  const hw=s.handwriting_detected?<span title="Handwritten" style={{fontSize:9,background:"rgba(255,193,7,0.15)",color:"#7A5200",border:"1px solid rgba(255,193,7,0.3)",borderRadius:4,padding:"1px 5px",marginLeft:5}}>✍</span>:null;
                  const amb=(s.handwriting_ambiguous||s.ambiguous)?<span title="Verify digits" style={{fontSize:9,background:"rgba(239,68,68,0.1)",color:"var(--error)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:4,padding:"1px 5px",marginLeft:4}}>⚠</span>:null;
                  return(<tr key={s.id}>
                    <td>{s.date}</td>
                    <td><span className="sup-name">{s.vendor_name||s.cust}</span>{hw}{amb}</td>
                    <td><span className="inv-num">{s.invoice_number||s.invNo||"—"}</span></td>
                    <td style={{fontSize:11}}>{s.category||s.cat||"—"}</td>
                    <td><span className="inv-amt">{(s.total_incl_vat||s.total||0).toFixed(2)}</span></td>
                    <td><span className="inv-amt c-warn">{(s.vat_amount||s.vat||0).toFixed(2)}</span></td>
                    <td><span style={{fontSize:10,fontWeight:700,color:cc,textTransform:"uppercase"}}>{conf}</span></td>
                  </tr>);
                })}
              </tbody>
            </table>
          </div>}
      </div>

    </div>);
  };

  const renderQuickBooks=()=>{
    const b64ToBuffer = b64 => {
      const bin = atob(b64);
      const buf = new Uint8Array(bin.length);
      for(let i=0;i<bin.length;i++) buf[i]=bin.charCodeAt(i);
      return buf.buffer;
    };
    const downloadB64 = (b64, filename) => triggerDownload(b64ToBuffer(b64), filename);
    const handleQbProcess = async () => {
      if(!qbFile) return;
      setQbProc(true); setQbResult(null); setQbError("");
      try {
        const result = await processQuickbooksReport(qbFile);
        setQbResult(result);
      } catch(err) {
        setQbError(err.message || "Processing failed");
      } finally { setQbProc(false); }
    };
    return(
    <div className="page">
      <div className="two-col" style={{alignItems:"start"}}>
        {/* ── LEFT: Upload + Process ── */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">📦 External VAT Report</span>
              {qbResult&&<span style={{fontSize:11,color:"var(--success)"}}>✓ Processed</span>}
            </div>
            <div className="panel-body">
              <p style={{fontSize:11,color:"var(--muted)",marginBottom:14,lineHeight:1.7}}>
                Upload an externally exported <strong>VAT Report CSV</strong>. The processor produces two formatted Excel workbooks — one for VAT Outputs and one for VAT Inputs — laid out to match the official ERS VAT schedule form.
              </p>

              {!qbProcessing&&!qbResult&&(
                <>
                  <div
                    onDrop={e=>{e.preventDefault();setQbDrag(false);const f=e.dataTransfer.files[0];if(f&&(f.name.endsWith(".csv")||f.type==="text/csv"))setQbFile(f);}}
                    onDragOver={e=>{e.preventDefault();setQbDrag(true);}}
                    onDragLeave={()=>setQbDrag(false)}
                    onClick={()=>qbRef.current?.click()}
                    className={`upload-zone ${qbDragOver?"drag-over":""}`}>
                    <div className="upload-icon">📊</div>
                    <div className="upload-title">Drop VAT Report CSV here</div>
                    <div className="upload-sub">CSV export from your accounting software — <span>click to browse</span></div>
                  </div>
                  <input ref={qbRef} type="file" accept=".csv,text/csv" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)setQbFile(f);}}/>
                  {qbFile&&(
                    <div style={{marginTop:14}}>
                      <div className="file-item">
                        <span style={{fontSize:16}}>📊</span>
                        <span className="file-name">{qbFile.name}</span>
                        <span className="file-size">{(qbFile.size/1024).toFixed(0)} KB</span>
                        <button className="file-remove" onClick={()=>setQbFile(null)}>✕</button>
                      </div>
                      <button className="btn btn-blue" style={{marginTop:10}} onClick={handleQbProcess}>
                        🚀 Process External Report
                      </button>
                    </div>
                  )}
                  {qbError&&<div style={{marginTop:10,fontSize:12,color:"var(--error)",background:"rgba(255,107,138,0.08)",border:"1px solid rgba(255,107,138,0.2)",borderRadius:10,padding:"10px 14px"}}>⚠ {qbError}</div>}
                </>
              )}

              {qbProcessing&&(
                <div style={{padding:"20px 0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                    <span style={{fontSize:28}}>📊</span>
                    <div>
                      <div style={{fontWeight:700,fontSize:14,color:"var(--ai-blue)"}}>Processing External VAT Report…</div>
                      <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Parsing CSV · Categorising transactions · Building Excel workbooks</div>
                    </div>
                  </div>
                  <div style={{background:"rgba(59,158,255,0.08)",borderRadius:99,height:4,overflow:"hidden"}}>
                    <div style={{height:"100%",background:"linear-gradient(90deg,var(--ai-deep),var(--ai-blue))",borderRadius:99,width:"100%",animation:"indeterminate 1.6s ease-in-out infinite"}}/>
                  </div>
                </div>
              )}

              {!qbProcessing&&qbResult&&(
                <div>
                  {/* Success header */}
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,background:"rgba(34,211,168,0.07)",border:"1px solid rgba(34,211,168,0.2)",borderRadius:14,padding:"14px 18px"}}>
                    <span style={{fontSize:28}}>✅</span>
                    <div>
                      <div style={{fontWeight:700,fontSize:14,color:"var(--success)"}}>External report processed successfully</div>
                      <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Two ERS-formatted Excel workbooks are ready</div>
                    </div>
                  </div>

                  {/* Download cards */}
                  <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
                    <div style={{background:"rgba(15,110,86,0.1)",border:"1px solid rgba(15,110,86,0.25)",borderRadius:12,padding:"14px 16px"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:"var(--success)"}}>📤 VAT Outputs Report</div>
                          <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>Outputs Summary · Zero Rate Output VAT</div>
                        </div>
                      </div>
                      <button className="btn btn-green btn-sm" style={{width:"100%"}} onClick={()=>downloadB64(qbResult.outputs_b64, qbResult.filename_outputs||"VAT_Outputs_Report.xlsx")}>
                        ⬇ Download VAT Outputs Excel
                      </button>
                    </div>

                    <div style={{background:"rgba(12,68,124,0.15)",border:"1px solid rgba(59,130,246,0.25)",borderRadius:12,padding:"14px 16px"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:"var(--ai-bright)"}}>📥 VAT Inputs Report</div>
                          <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>Inputs Summary · Standard Rate 15% · Zero Rate Inputs</div>
                        </div>
                      </div>
                      <button className="btn btn-blue btn-sm" style={{width:"100%"}} onClick={()=>downloadB64(qbResult.inputs_b64, qbResult.filename_inputs||"VAT_Inputs_Report.xlsx")}>
                        ⬇ Download VAT Inputs Excel
                      </button>
                    </div>
                  </div>

                  <button className="btn btn-ghost" style={{fontSize:12}} onClick={()=>{setQbResult(null);setQbFile(null);}}>↺ Process Another Report</button>
                </div>
              )}
            </div>
          </div>

          {/* Info card */}
          <div className="panel" style={{border:"1px solid rgba(59,158,255,0.12)"}}>
            <div className="panel-body" style={{padding:16}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--ai-blue)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>About this tool</div>
              <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.8}}>
                <div style={{marginBottom:6}}>📊 <strong>Supported exports:</strong> External accounting software VAT Report (CSV format)</div>
                <div style={{marginBottom:6}}>📤 <strong>Outputs workbook:</strong> ERS Output Tax schedule — standard-rated, zero-rated, and exempt supplies with customer TINs</div>
                <div style={{marginBottom:6}}>📥 <strong>Inputs workbook:</strong> ERS Input Tax schedule — standard-rated and zero-rated domestic purchases with supplier TINs</div>
                <div>🎨 Styling matches the VATICA navy/blue palette and conforms to ERS VAT form layout</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Instructions / empty state ── */}
        <div className="panel">
          <div className="panel-header"><span className="panel-title">How to Export from Your Accounting Software</span></div>
          <div className="panel-body" style={{fontSize:12,color:"var(--muted)",lineHeight:1.9}}>
            <div style={{marginBottom:16}}>
              <div style={{fontWeight:700,color:"var(--text)",marginBottom:6}}>Online Accounting Software</div>
              <ol style={{paddingLeft:18,display:"flex",flexDirection:"column",gap:4}}>
                <li>Go to <strong>Reports</strong> → search <em>VAT Detail</em></li>
                <li>Set your tax period date range</li>
                <li>Click <strong>Export</strong> → <strong>Export to CSV</strong></li>
                <li>Upload the downloaded CSV here</li>
              </ol>
            </div>
            <div className="divider"/>
            <div style={{marginTop:16}}>
              <div style={{fontWeight:700,color:"var(--text)",marginBottom:6}}>Desktop Accounting Software</div>
              <ol style={{paddingLeft:18,display:"flex",flexDirection:"column",gap:4}}>
                <li>Go to <strong>Reports</strong> → <strong>VAT Reports</strong></li>
                <li>Select <em>VAT Report</em> for your bi-monthly period</li>
                <li>Click <strong>Export</strong> → choose <strong>CSV</strong></li>
                <li>Upload the downloaded CSV here</li>
              </ol>
            </div>
            <div className="divider"/>
            <div style={{marginTop:16,background:"rgba(59,158,255,0.05)",border:"1px solid rgba(59,158,255,0.12)",borderRadius:10,padding:"12px 14px",fontSize:11}}>
              <strong style={{color:"var(--ai-blue)"}}>Note:</strong> The CSV must contain both Output (sales) and Input (purchases) rows. The processor automatically detects and splits them.
            </div>
          </div>
        </div>
      </div>
    </div>);
  };

  const renderZReports=()=>{
    return(
    <div className="page">
      <div className="two-col" style={{alignItems:"start"}}>
        {/* ── LEFT: Upload + Process ── */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <ClientSelector value={zrClient} onChange={setZrClient}/>

          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">🧾 Z-Report Extraction</span>
              {zrBuffer&&<span style={{fontSize:11,color:"var(--success)"}}>✓ Ready to download</span>}
            </div>
            <div className="panel-body">
              <p style={{fontSize:11,color:"var(--muted)",marginBottom:14,lineHeight:1.7}}>
                Upload a <strong>Z-Report PDF</strong> (till end-of-day report) — one file can contain any number of individual reports. The extractor produces a formatted Excel workbook with a Z-Report Summary, a Tender Breakdown, and a Review Issues sheet for anything flagged low-confidence.
              </p>

              {!zrProcessing&&!zrBuffer&&(
                <>
                  <div
                    onDrop={e=>{e.preventDefault();setZrDrag(false);const f=e.dataTransfer.files[0];if(f&&f.type==="application/pdf")setZrFile(f);}}
                    onDragOver={e=>{e.preventDefault();setZrDrag(true);}}
                    onDragLeave={()=>setZrDrag(false)}
                    onClick={()=>zrRef.current?.click()}
                    className={`upload-zone ${zrDragOver?"drag-over":""}`}>
                    <div className="upload-icon">🧾</div>
                    <div className="upload-title">Drop Z-Report PDF here</div>
                    <div className="upload-sub">Scanned or exported till report — <span>click to browse</span></div>
                  </div>
                  <input ref={zrRef} type="file" accept="application/pdf" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)setZrFile(f);}}/>

                  <div style={{marginTop:14}}>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--ai-blue)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>
                      TIN &amp; Customer <span style={{color:"var(--muted)",fontWeight:400,textTransform:"none",letterSpacing:"normal"}}>(optional)</span>
                    </div>
                    <p style={{fontSize:11,color:"var(--muted)",marginBottom:10,lineHeight:1.6}}>
                      A Z-Report covers many customers in one till closing, so these aren't printed on the slip. Fill them in here to apply <strong>one TIN and one Customer name to every row</strong> in this batch, or leave blank and fill them in directly on the Excel later — either way, extraction runs the same.
                    </p>
                    <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                      <input
                        type="text"
                        placeholder="TIN (optional)"
                        value={zrTin}
                        onChange={e=>setZrTin(e.target.value)}
                        style={{
                          flex:"1 1 160px",minWidth:140,
                          background:"var(--panel)",border:"1px solid var(--border)",
                          borderRadius:10,padding:"10px 12px",fontSize:13,
                          color:"var(--text)",fontFamily:"inherit",outline:"none",
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Customer name (optional)"
                        value={zrCustomer}
                        onChange={e=>setZrCustomer(e.target.value)}
                        style={{
                          flex:"1 1 200px",minWidth:160,
                          background:"var(--panel)",border:"1px solid var(--border)",
                          borderRadius:10,padding:"10px 12px",fontSize:13,
                          color:"var(--text)",fontFamily:"inherit",outline:"none",
                        }}
                      />
                    </div>
                  </div>

                  {zrFile&&(
                    <div style={{marginTop:14}}>
                      <div className="file-item">
                        <span style={{fontSize:16}}>📄</span>
                        <span className="file-name">{zrFile.name}</span>
                        <span className="file-size">{(zrFile.size/1024).toFixed(0)} KB</span>
                        <button className="file-remove" onClick={()=>setZrFile(null)}>✕</button>
                      </div>
                      <button className="btn btn-teal" style={{marginTop:10}} onClick={handleZrProcess}>
                        🚀 Process Z - Slips
                      </button>
                    </div>
                  )}
                  {zrError&&<div style={{marginTop:10,fontSize:12,color:"var(--error)",background:"rgba(255,107,138,0.08)",border:"1px solid rgba(255,107,138,0.2)",borderRadius:10,padding:"10px 14px"}}>⚠ {zrError}</div>}
                </>
              )}

              {zrProcessing&&(
                <div style={{padding:"20px 0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                    <span style={{fontSize:28}}>🧾</span>
                    <div>
                      <div style={{fontWeight:700,fontSize:14,color:"var(--ai-blue)"}}>Extracting Z - Slips…</div>
                      <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Reading pages · Extracting totals & tender lines · Building Excel workbook</div>
                    </div>
                  </div>
                  <div style={{background:"rgba(59,158,255,0.08)",borderRadius:99,height:4,overflow:"hidden"}}>
                    <div style={{height:"100%",background:"linear-gradient(90deg,var(--ai-deep),var(--ai-blue))",borderRadius:99,width:"100%",animation:"indeterminate 1.6s ease-in-out infinite"}}/>
                  </div>
                </div>
              )}

              {!zrProcessing&&zrBuffer&&(
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,background:"rgba(34,211,168,0.07)",border:"1px solid rgba(34,211,168,0.2)",borderRadius:14,padding:"14px 18px"}}>
                    <span style={{fontSize:28}}>✅</span>
                    <div>
                      <div style={{fontWeight:700,fontSize:14,color:"var(--success)"}}>Z - Slips processed successfully</div>
                      <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Excel workbook is ready to download{zrClient?` · saved to ${zrClient.name}`:""}</div>
                    </div>
                  </div>

                  <button className="btn btn-teal" style={{marginBottom:8}} onClick={()=>triggerDownload(zrBuffer, zrFilename)}>⬇ Download Z-Report Excel</button>
                  <button className="btn btn-ghost btn-sm" style={{marginTop:4}} onClick={()=>{setZrBuffer(null);setZrFile(null);setZrMergeFiles([]);setZrError("");setZrTin("");setZrCustomer("");}}>↺ New PDF</button>

                  {/* Merge with one or more previously downloaded Z-Report files */}
                  <div className="divider"/>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--ai-blue)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>Combine with earlier Z-Report files</div>
                  <p style={{fontSize:11,color:"var(--muted)",marginBottom:12,lineHeight:1.6}}>
                    Already have earlier Z-Report Excel workbooks for this period (previous batches, days, or weeks)? Choose as many as you like — they'll all fold into one workbook, automatically re-sorted <strong>oldest → newest</strong> by report date.
                  </p>

                  <div className="file-upload-row">
                    <input
                      ref={zrMergeRef}
                      type="file"
                      accept=".xlsx"
                      multiple
                      style={{display:"none"}}
                      onChange={e=>{
                        const incoming = Array.from(e.target.files||[]);
                        if(incoming.length) setZrMergeFiles(prev=>[...prev,...incoming]);
                        e.target.value = "";
                      }}
                    />
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={()=>zrMergeRef.current?.click()}
                    >
                      Choose files
                    </button>
                    <span className={`file-upload-name${zrMergeFiles.length ? ' file-upload-name--set' : ''}`}>
                      {zrMergeFiles.length
                        ? `${zrMergeFiles.length} file${zrMergeFiles.length>1?'s':''} chosen`
                        : 'No file chosen'}
                    </span>
                  </div>

                  {zrMergeFiles.length>0&&(
                    <div style={{marginTop:12}}>
                      {zrMergeFiles.map((f,i)=>(
                        <div key={`${f.name}-${i}`} className="file-item" style={{marginBottom:6}}>
                          <span style={{fontSize:16}}>📊</span>
                          <span className="file-name">{f.name}</span>
                          <span className="file-size">{(f.size/1024).toFixed(0)} KB</span>
                          <button className="file-remove" onClick={()=>setZrMergeFiles(prev=>prev.filter((_,idx)=>idx!==i))}>✕</button>
                        </div>
                      ))}
                      <div style={{display:"flex",gap:8,alignItems:"center",marginTop:10}}>
                        <button className="btn btn-blue btn-sm" disabled={zrMerging} onClick={handleZrMerge}>
                          {zrMerging?"Merging…":`⊕ Merge ${zrMergeFiles.length+1} File${zrMergeFiles.length+1>1?"s":""} & Download`}
                        </button>
                        <button className="btn btn-ghost btn-sm" disabled={zrMerging} onClick={()=>setZrMergeFiles([])}>Clear all</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: Info card ── */}
        <div className="panel">
          <div className="panel-header"><span className="panel-title">About Z-Report Extraction</span></div>
          <div className="panel-body" style={{fontSize:12,color:"var(--muted)",lineHeight:1.9}}>
            <div style={{marginBottom:6}}>🧾 <strong>Z-Report Summary:</strong> one row per report — taxable total, standard/zero-rated, exempted, tax amount, tendered, returns, and discounts</div>
            <div style={{marginBottom:6}}>🪪 <strong>TIN &amp; Customer:</strong> optional — apply one TIN and one Customer name to the whole batch above, or leave blank and fill them (plus Exempted) directly on the Excel</div>
            <div style={{marginBottom:6}}>💳 <strong>Tender Breakdown:</strong> every payment-method line per report, with its share of the total tendered</div>
            <div style={{marginBottom:6}}>⚠️ <strong>Review Issues:</strong> low-confidence or handwriting-ambiguous reports flagged for a manual check</div>
            <div style={{marginBottom:16}}>📎 Attach a client above to save this report to their profile — the pages also count toward Page Count under Sales.</div>
            <div className="divider"/>
            <div style={{marginTop:16,background:"rgba(59,158,255,0.05)",border:"1px solid rgba(59,158,255,0.12)",borderRadius:10,padding:"12px 14px",fontSize:11}}>
              <strong style={{color:"var(--ai-blue)"}}>Note:</strong> Reports with no readable tender lines still appear in the Summary sheet — see its own row for totals even if it's skipped in Tender Breakdown.
            </div>
          </div>
        </div>
      </div>
    </div>);
  };


  const renderVatReturn=()=>{
    const selectedClientFull = clientList.find(c => String(c.id) === String(vatClient?.id));
    const clientTinForVat = selectedClientFull?.tin || selectedClientFull?.vat_number || "";
    return (
    <div className="page">
      <ClientSelector value={vatClient} onChange={setVatClient}/>
      <VatWorkflow
        clientId={vatClient?.id}
        clientName={vatClient?.name}
        clientTin={clientTinForVat}
      />
    </div>
    );
  };

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{styles}</style>
      <div className={`sidebar-overlay ${sideOpen?"open":""}`} onClick={()=>setSide(false)}/>
      <div className={`app ${(mainTab==="overview"&&!selectedClientId)?"sidebar-autohide":""}`}>

        {/* ── SIDEBAR ── */}
        {/* Invisible strip along the left edge — hovering it (or the sidebar
            itself) reveals the sidebar on desktop; moving away hides it again. */}
        <div className="sidebar-hover-zone" />
        <aside className={`sidebar ${sideOpen?"open":""}`}>
          <div className="sidebar-brand">
            <div className="brand-name">VATICA AI</div>
            <div className="brand-tag">VAT Intelligence · Eswatini</div>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-label">Dashboard</div>
            <div className={`nav-item ${mainTab==="overview"&&!selectedClientId?"active":""}`} onClick={()=>goTo("overview")}>
              <span className="nav-icon">⬡</span>Overview
            </div>

            <div className="nav-label">Tools</div>

            {/* VAT */}
            <div className={`nav-item ${mainTab==="vat"?"parent-active":""}`}
              onClick={()=>{
                const isOpen=sidebarExpanded.has("vat");
                setSidebarExpanded(prev=>{const s=new Set(prev);isOpen?s.delete("vat"):s.add("vat");return s;});
                if(!isOpen&&mainTab!=="vat") goTo("vat","purchases");
              }}>
              <span className="nav-icon">📊</span>
              <span style={{flex:1}}>VAT Automation</span>
              {(processing||salesExtracting)&&mainTab!=="vat"
                ?<span className="nav-busy-dot" title="Extraction in progress"/>
                :<span className={`nav-chevron ${sidebarExpanded.has("vat")?"open":""}`}>›</span>
              }
            </div>
            <div className={`nav-sub-list ${sidebarExpanded.has("vat")?"open":""}`}>
              {VAT_SUBS.map(s=>(
                <div key={s.id} className={`nav-sub-item ${mainTab==="vat"&&vatSub===s.id?"active":""}`}
                  onClick={()=>{goTo("vat",s.id);setSide(false);}}>
                  <span className="nav-sub-dot"/>{s.icon} {s.label}
                </div>
              ))}
            </div>

            {/* Reports (QB Reports + Z - Slips) */}
            <div className={`nav-item ${mainTab==="reports"?"parent-active":""}`}
              onClick={()=>{
                const isOpen=sidebarExpanded.has("reports");
                setSidebarExpanded(prev=>{const s=new Set(prev);isOpen?s.delete("reports"):s.add("reports");return s;});
                if(!isOpen&&mainTab!=="reports") goTo("reports","qb");
              }}>
              <span className="nav-icon">🗂️</span>
              <span style={{flex:1}}>Reports</span>
              {(qbProcessing||zrProcessing)&&mainTab!=="reports"
                ?<span className="nav-busy-dot" title="Processing in progress"/>
                :<span className={`nav-chevron ${sidebarExpanded.has("reports")?"open":""}`}>›</span>
              }
            </div>
            <div className={`nav-sub-list ${sidebarExpanded.has("reports")?"open":""}`}>
              {REPORTS_SUBS.map(s=>(
                <div key={s.id} className={`nav-sub-item ${mainTab==="reports"&&reportsSub===s.id?"active":""}`}
                  onClick={()=>{goTo("reports",s.id);setSide(false);}}>
                  <span className="nav-sub-dot"/>{s.icon} {s.label}
                </div>
              ))}
            </div>

            {/* Bank Statements */}
            <div className={`nav-item ${mainTab==="bank"?"parent-active":""}`}
              onClick={()=>{
                const isOpen=sidebarExpanded.has("bank");
                setSidebarExpanded(prev=>{const s=new Set(prev);isOpen?s.delete("bank"):s.add("bank");return s;});
                if(!isOpen&&mainTab!=="bank") goTo("bank","current");
              }}>
              <span className="nav-icon">🏦</span>
              <span style={{flex:1}}>Bank Statements</span>
              {isBankBusy&&mainTab!=="bank"
                ?<span className="nav-busy-dot" title="Extraction in progress"/>
                :<span className={`nav-chevron ${sidebarExpanded.has("bank")?"open":""}`}>›</span>
              }
            </div>
            <div className={`nav-sub-list ${sidebarExpanded.has("bank")?"open":""}`}>
              {BANK_SUBS.map(s=>(
                <div key={s.id} className={`nav-sub-item ${mainTab==="bank"&&bankSub===s.id?"active":""}`}
                  onClick={()=>{goTo("bank",s.id);setSide(false);}}>
                  <span className="nav-sub-dot"/>{s.icon} {s.label}
                </div>
              ))}
            </div>

            <div className="nav-label">Management</div>
            <div className={`nav-item ${mainTab==="clients"?"active":""}`} onClick={()=>goTo("clients")}>
              <span className="nav-icon">👥</span>Clients
            </div>
            <div className={`nav-item ${mainTab==="usage"?"active":""}`} onClick={()=>goTo("usage")}>
              <span className="nav-icon">📄</span>Page Count
            </div>
          </nav>

          <div className="sidebar-footer">
            {!isAdminView&&(
              <div className="user-pill">
                <div className="user-avatar">{initials}</div>
                <div><div className="user-name">{storedUsername}</div><div className="user-plan">Active Client</div></div>
              </div>
            )}
            {isAdminView
              ?<button className="logout-btn" onClick={()=>onBackToAdmin?.()}>← Back to Admin</button>
              :<button className="logout-btn" onClick={()=>{ clearActivityTimestamp(); onLogout?.(); }}>← Sign out</button>}
          </div>
        </aside>

        {/* ── MAIN ── */}
        <div className="main">
          <header className="topbar">
            <div className="topbar-left">
              <button className="hamburger" onClick={()=>setSide(s=>!s)}>☰</button>
            </div>
            {(mainTab==="overview"&&!selectedClientId)
              ? <div className="topbar-brand">
                  <div className="topbar-brand-name">VATICA AI</div>
                  <div className="topbar-brand-tag">VAT Intelligence · Eswatini</div>
                </div>
              : <span className="topbar-title" style={{textAlign:"center",justifySelf:"center"}}>{pageTitle()}</span>}
            <div className="topbar-right">
              <div className="api-status"><div className="status-dot"/> API Active</div>
            </div>
          </header>

          {!selectedClientId&&mainTab==="overview"  && renderOverview()}
          {!selectedClientId&&mainTab==="vat"       && vatSub==="purchases" && renderPurchases()}
          {!selectedClientId&&mainTab==="vat"       && vatSub==="sales"     && renderSales()}

          {/* PersistentRoute keeps the VAT Return / ERS generator mounted (hidden
              when inactive) so in-progress taxpayer details, uploaded schedules,
              and document selections survive switching to Purchases/Sales
              sub-tabs or away to another main tab entirely — same
              fix as the Bank Statement one below, applied to the ERS side. */}
          <PersistentRoute active={!selectedClientId && mainTab==="vat" && vatSub==="vatreturn"}>
            {renderVatReturn()}
          </PersistentRoute>

          {/* PersistentRoute keeps BankStatementWorkflow mounted (hidden when inactive)
              so in-flight extractions survive navigation away from the Bank tab. */}
          <PersistentRoute active={!selectedClientId && mainTab==="bank"}>
            <BankStatementWorkflow bankSub={bankSub} onSubTabChange={setBankSub}/>
          </PersistentRoute>

          {!selectedClientId&&mainTab==="reports"   && reportsSub==="qb"      && renderQuickBooks()}
          {!selectedClientId&&mainTab==="reports"   && reportsSub==="zreport" && renderZReports()}

          {!selectedClientId&&mainTab==="connectors" && renderConnectors()}

          {!selectedClientId&&mainTab==="usage"     && <PageUsage/>}
          {!selectedClientId&&mainTab==="clients"   && <ClientsManager onSelectClient={id=>{setSelectedClientId(id);setMainTab(null);setSide(false);}}/>}

          {selectedClientId&&<ClientProfile clientId={selectedClientId} onBack={()=>{setSelectedClientId(null);setMainTab("clients");}}/>}
        </div>
      </div>
    
      <ConfirmModal state={confirmModal} onClose={() => setConfirmModal(null)} />
    </>
  );
}